// ============================================================
// investorApi.js
// Supabase query functions for the Investor Portal & Manager Dashboard.
//
// PATTERN:
//   Same style as auth.js — each function is a simple async wrapper
//   around a Supabase query. No classes, no context — just functions.
//
// WHO CALLS THESE:
//   - hooks/useInvestorPortal.js  → investor-facing hooks
//   - hooks/useManagerDashboard.js → manager-facing hooks
//   - Phase3Dashboard.jsx   → manager actions (invite, NAV update, etc.)
//
// SECURITY:
//   Row Level Security (RLS) in Supabase controls who can see what.
//   These functions just send the query — Supabase enforces the rules.
//   An investor calling getAllInvestors() would get an empty result
//   because RLS blocks them from seeing other investors' data.
// ============================================================

import { supabase } from './supabase';
import { getUser }  from './auth';
import { syncFundSubscribersToCrm } from './subscriberCrmSync';

const isPlaceholderProfileName = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return !normalized || normalized === 'trader' || normalized === 'subscriber' || normalized === 'unknown';
};

const resolveProfileDisplayName = (value, fallback = 'Subscriber') => (
  isPlaceholderProfileName(value) ? fallback : String(value).trim()
);

const SUBSCRIBER_DOCUMENT_BUCKET = 'subscriber-documents';
const SUBSCRIBER_DEPOSIT_SCREENSHOT_BUCKET = 'subscriber-deposit-screenshots';
const DEFAULT_DOCUMENT_VERSION = 'local-draft-v2';
const DEFAULT_TAX_RATE = 0.30;
const PAYOUT_WINDOW_AUTO_LOCK_DAYS = 7;

export const BUSINESS_CONTACT_EMAIL = 'carlton.hooks@cnrfamily.com';
export const ZELLE_DEPOSIT_EMAIL = 'carlton.hooks@gmail.com';
export const SUBSCRIBER_DEPOSIT_STATUSES = [
  'Pending Submission',
  'Sent by Subscriber',
  'Received',
  'Confirmed',
  'Active Next Cycle',
  'Needs Review',
];

export const formatSubscriberAccountId = (value) => String(value || '').trim() || 'Pending ID';

export const normalizeZelleProfile = ({ zelleName = '', zelleEmail = '', zellePhone = '' } = {}) => ({
  zelleName: String(zelleName || '').trim(),
  zelleEmail: String(zelleEmail || '').trim(),
  zellePhone: String(zellePhone || '').trim(),
});

export const validateZelleProfile = (profile = {}) => {
  const normalized = normalizeZelleProfile(profile);
  if (!normalized.zelleName) {
    return { profile: normalized, error: { message: 'Enter the recipient name registered with Zelle.' } };
  }
  if (!normalized.zelleEmail && !normalized.zellePhone) {
    return { profile: normalized, error: { message: 'Enter either a Zelle email or Zelle phone number.' } };
  }
  return { profile: normalized, error: null };
};

export const buildZelleMemo = ({ accountId, subscriberName, amount }) => {
  return `${formatSubscriberAccountId(accountId)} - ${String(subscriberName || 'Subscriber').trim()} - Dollar Amount Sending`;
};

const buildLocalSubscriberAccountId = (seed) => {
  let hash = 0;
  const source = String(seed || '');
  for (let i = 0; i < source.length; i += 1) {
    hash = ((hash << 5) - hash) + source.charCodeAt(i);
    hash |= 0;
  }
  const number = Math.abs(hash % 9000) + 1000;
  return `CNRF-01-${String(number).padStart(4, '0')}`;
};

const slugifyDocument = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'document';

const isMissingSchemaObjectError = (error, objectName) => {
  const message = String(error?.message || error?.details || error?.hint || '').toLowerCase();
  return message.includes(`public.${objectName}`.toLowerCase())
    || message.includes(objectName.toLowerCase());
};

const getPayoutWindowStatus = (fundState) => {
  const isOpen = Boolean(fundState?.payout_window_open);
  const openedAt = fundState?.payout_window_opened_at
    ? new Date(fundState.payout_window_opened_at)
    : null;

  if (!isOpen || !openedAt || Number.isNaN(openedAt.getTime())) {
    return {
      isOpen,
      openedAt: fundState?.payout_window_opened_at || null,
      expiresAt: null,
      expired: false,
      msRemaining: null,
      daysRemaining: null,
    };
  }

  const expiresAtDate = new Date(openedAt.getTime() + PAYOUT_WINDOW_AUTO_LOCK_DAYS * 24 * 60 * 60 * 1000);
  const msRemaining = expiresAtDate.getTime() - Date.now();
  const expired = msRemaining <= 0;

  return {
    isOpen,
    openedAt: openedAt.toISOString(),
    expiresAt: expiresAtDate.toISOString(),
    expired,
    msRemaining,
    daysRemaining: expired ? 0 : Math.ceil(msRemaining / (24 * 60 * 60 * 1000)),
  };
};

const autoCloseExpiredPayoutWindow = async (fundState) => {
  const status = getPayoutWindowStatus(fundState);
  if (!status.isOpen || !status.expired) {
    return { fundState, status };
  }

  const normalizedState = {
    ...fundState,
    payout_window_open: false,
    payout_window_opened_at: null,
    last_updated: new Date().toISOString(),
  };

  await supabase
    .from('fund_state')
    .update({
      payout_window_open: false,
      payout_window_opened_at: null,
      last_updated: normalizedState.last_updated,
    })
    .eq('id', fundState.id);

  return {
    fundState: normalizedState,
    status: {
      ...status,
      isOpen: false,
      expired: true,
      openedAt: null,
      expiresAt: status.expiresAt,
      msRemaining: 0,
      daysRemaining: 0,
    },
  };
};

const buildApplicationSnapshot = ({
  userId,
  title,
  slug,
  version,
  acceptedAt,
  method,
  renderedText,
  signerName = null,
  signerEmail = null,
}) => ({
  userId,
  title,
  slug,
  version,
  acceptedAt,
  acceptanceMethod: method,
  renderedText,
  signerName,
  signerEmail,
});

const storeDocumentSnapshot = async ({
  userId,
  title,
  slug,
  version,
  acceptedAt,
  method,
  renderedText,
  signerName,
  signerEmail,
}) => {
  const path = `${userId}/${slug}-${version}.json`;
  const payload = buildApplicationSnapshot({
    userId,
    title,
    slug,
    version,
    acceptedAt,
    method,
    renderedText,
    signerName,
    signerEmail,
  });

  const { error } = await supabase.storage
    .from(SUBSCRIBER_DOCUMENT_BUCKET)
    .upload(path, JSON.stringify(payload, null, 2), {
      contentType: 'application/json',
      upsert: true,
    });

  if (error) {
    return { path: null, payload, error };
  }

  return { path, payload, error: null };
};

const saveApplicationDocuments = async (userId, legalConsents = null) => {
  if (!legalConsents?.documents?.length) {
    return { error: null, documents: [] };
  }

  const acceptedAt = legalConsents.reviewedAt || new Date().toISOString();
  const version = legalConsents.version || DEFAULT_DOCUMENT_VERSION;
  const acceptanceMethod = legalConsents.acceptanceMethod || 'electronic_acknowledgment';
  const signerName = String(legalConsents.signerName || legalConsents.username || '').trim() || null;
  const signerEmail = String(legalConsents.signerEmail || legalConsents.email || '').trim() || null;

  const rows = [];

  for (const document of legalConsents.documents) {
    const title = document.title || 'Subscriber document';
    const slug = document.slug || slugifyDocument(title);
    const snapshot = await storeDocumentSnapshot({
      userId,
      title,
      slug,
      version,
      acceptedAt,
      method: acceptanceMethod,
      renderedText: document.renderedText || document.summary || '',
      signerName,
      signerEmail,
    });

    rows.push({
      user_id: userId,
      document_slug: slug,
      title,
      version,
      accepted_at: acceptedAt,
      acceptance_method: acceptanceMethod,
      snapshot_path: snapshot.path,
      snapshot_payload: snapshot.payload,
    });
  }

  const { error } = await supabase
    .from('subscriber_application_documents')
    .upsert(rows, { onConflict: 'user_id,document_slug,version' });

  if (error && isMissingSchemaObjectError(error, 'subscriber_application_documents')) {
    return {
      error: {
        ...error,
        message: 'Signed onboarding documents are not configured in this database yet. Run migration 008_subscriber_onboarding_and_distribution_refresh.sql first.',
      },
      documents: [],
    };
  }

  return { error, documents: rows };
};

const logSubscriberDelivery = async ({
  userId,
  documentSlug = null,
  title = null,
  deliveryStatus = 'recorded',
  destination = null,
  details = {},
}) => supabase
  .from('subscriber_delivery_log')
  .insert({
    user_id: userId,
    document_slug: documentSlug,
    title,
    delivery_channel: 'portal_email_record',
    delivery_status: deliveryStatus,
    destination,
    delivered_at: deliveryStatus === 'failed' ? null : new Date().toISOString(),
    details,
  });

const markNotionMirrorPending = async ({ table, recordId }) => {
  // The browser app cannot safely hold Notion write credentials. Records stay
  // marked pending until the Notion connector/backend sync updates these fields.
  if (!table || !recordId) return;

  await supabase
    .from(table)
    .update({
      notion_sync_status: 'pending',
      notion_error: null,
    })
    .eq('id', recordId);
};

const normalizeReferenceId = (value) => String(value || '')
  .trim()
  .replace(/\s+/g, '-')
  .slice(0, 120) || `ref-${Date.now()}`;

const isMissingLedgerRpcError = (error) => {
  const message = String(error?.message || error?.details || error?.hint || '').toLowerCase();
  return message.includes('post_journal_entry')
    || message.includes('journal_entries')
    || message.includes('journal_lines')
    || error?.code === 'PGRST202';
};

const postJournalEntry = async ({
  fundId,
  investorId = null,
  entryType,
  amount,
  description,
  referenceId,
  sourceTable = null,
  sourceId = null,
  notes = null,
  lines,
}) => {
  const normalizedAmount = Number(amount || 0);
  if (!fundId || !entryType || !Number.isFinite(normalizedAmount) || normalizedAmount <= 0) {
    return { data: null, error: null, warning: null };
  }

  const { data, error } = await supabase.rpc('post_journal_entry', {
    p_fund_id: fundId,
    p_investor_id: investorId,
    p_entry_type: entryType,
    p_description: description || entryType,
    p_reference_id: normalizeReferenceId(referenceId),
    p_source_table: sourceTable,
    p_source_id: sourceId,
    p_notes: notes,
    p_metadata: {},
    p_lines: lines.map((line) => ({
      account_code: line.accountCode,
      debit: Number(line.debit || 0),
      credit: Number(line.credit || 0),
      investor_id: line.investorId || investorId,
      memo: line.description || description || entryType,
    })),
  });

  if (error && isMissingLedgerRpcError(error)) {
    return {
      data: null,
      error: null,
      warning: 'Ledger RPC unavailable until database/021_ledger_audit_lp_exports.sql is applied.',
    };
  }

  return { data, error, warning: null };
};

const postContributionJournal = ({
  fundId,
  investorId,
  amount,
  sourceTable,
  sourceId,
  referenceId,
  description = 'Subscriber contribution posted',
  entryType = 'contribution',
}) => postJournalEntry({
  fundId,
  investorId,
  entryType,
  amount,
  description,
  referenceId,
  sourceTable,
  sourceId,
  lines: [
    { accountCode: '1000', debit: amount },
    { accountCode: '3000', credit: amount },
  ],
});

const postDistributionJournal = ({
  fundId,
  investorId,
  amount,
  taxWithholding = 0,
  fee = 0,
  sourceId,
  referenceId,
  period,
  reinvest = false,
}) => {
  const net = Number(amount || 0);
  const tax = Number(taxWithholding || 0);
  const managerFee = Number(fee || 0);
  const gross = net + tax + managerFee;
  if (gross <= 0) return { data: null, error: null, warning: null };

  const lines = [
    { accountCode: '4000', debit: gross, description: `${period || 'Distribution'} allocation` },
  ];
  if (tax > 0) lines.push({ accountCode: '2000', credit: tax, description: 'Tax reserve withheld' });
  if (managerFee > 0) lines.push({ accountCode: '3100', credit: managerFee, description: 'GP performance allocation' });
  lines.push({
    accountCode: reinvest ? '3000' : '2100',
    credit: net,
    description: reinvest ? 'Distribution reinvested to capital account' : 'Distribution payable to subscriber',
  });

  return postJournalEntry({
    fundId,
    investorId,
    entryType: reinvest ? 'profit_allocation' : 'distribution',
    amount: gross,
    description: `${period || 'Distribution'} ${reinvest ? 'reinvestment' : 'payout'} posted`,
    referenceId,
    sourceTable: 'distributions',
    sourceId,
    lines,
  });
};

const postFundValueSettlementJournal = ({
  fundId,
  settlementId,
  grossFundValue,
  postPayoutValue,
  payoutAmount,
  valuationDate,
}) => {
  const gross = Number(grossFundValue || 0);
  const postPayout = Number(postPayoutValue || 0);
  const payout = Number(payoutAmount || 0);
  if (gross <= 0) return { data: null, error: null, warning: null };

  const lines = [];
  if (postPayout > 0) lines.push({ accountCode: '1100', debit: postPayout, description: 'Post-payout brokerage equity' });
  if (payout > 0) lines.push({ accountCode: '2100', debit: payout, description: 'Payouts reserved from fund value settlement' });
  lines.push({ accountCode: '4000', credit: gross, description: 'Fund value settlement' });

  return postJournalEntry({
    fundId,
    entryType: 'fund_value_settlement',
    amount: gross,
    description: `Fund value settlement ${valuationDate || ''}`.trim(),
    referenceId: `fund-value-settlement:${settlementId}`,
    sourceTable: 'fund_value_settlements',
    sourceId: settlementId,
    lines,
  });
};

const uploadDepositScreenshot = async ({ subscriberId, file }) => {
  if (!file) return { url: null, error: null };

  const safeName = String(file.name || 'deposit-screenshot')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'deposit-screenshot';
  const path = `${subscriberId}/${Date.now()}-${safeName}`;

  const { error } = await supabase.storage
    .from(SUBSCRIBER_DEPOSIT_SCREENSHOT_BUCKET)
    .upload(path, file, { upsert: false, contentType: file.type || undefined });

  if (error) return { url: null, error };
  return { url: path, error: null };
};

export const updatePayoutProfile = async ({
  userId,
  zelleName,
  zelleEmail,
  zellePhone,
}) => {
  const { profile, error: validationError } = validateZelleProfile({ zelleName, zelleEmail, zellePhone });
  if (validationError) return { data: null, error: validationError };

  const targetUserId = userId || (await getUser())?.id;
  if (!targetUserId) return { data: null, error: { message: 'User not found.' } };

  const { data, error } = await supabase
    .from('profiles')
    .update({
      zelle_name: profile.zelleName,
      zelle_email: profile.zelleEmail || null,
      zelle_phone: profile.zellePhone || null,
      zelle_updated_at: new Date().toISOString(),
    })
    .eq('id', targetUserId)
    .select('id, zelle_name, zelle_email, zelle_phone, zelle_updated_at')
    .limit(1)
    .maybeSingle();

  if (!error && !data) {
    const { data: upserted, error: upsertError } = await supabase
      .from('profiles')
      .upsert({
        id: targetUserId,
        zelle_name: profile.zelleName,
        zelle_email: profile.zelleEmail || null,
        zelle_phone: profile.zellePhone || null,
        zelle_updated_at: new Date().toISOString(),
      }, { onConflict: 'id' })
      .select('id, zelle_name, zelle_email, zelle_phone, zelle_updated_at')
      .limit(1)
      .maybeSingle();

    return { data: upserted, error: upsertError };
  }

  return { data, error };
};

export const getSubscriberDeposits = async (subscriberId) => {
  if (!subscriberId) return { data: [], error: null };

  const { data, error } = await supabase
    .from('subscriber_deposits')
    .select('*')
    .eq('subscriber_id', subscriberId)
    .order('submitted_at', { ascending: false });

  return { data: data || [], error };
};

export const getSubscriberRedemptionInstructions = async (subscriberId) => {
  if (!subscriberId) return { data: [], error: null };

  const { data, error } = await supabase
    .from('subscriber_redemption_instructions')
    .select('*')
    .eq('subscriber_id', subscriberId)
    .order('confirmed_at', { ascending: false })
    .order('created_at', { ascending: false });

  return { data: data || [], error };
};

export const getSubscriberApplicationDocuments = async (userId = null) => {
  const targetUserId = userId || (await getUser())?.id;
  if (!targetUserId) return { data: [], error: { message: 'User not found.' } };

  const { data, error } = await supabase
    .from('subscriber_application_documents')
    .select('id, document_slug, title, version, accepted_at, acceptance_method, snapshot_path, snapshot_payload, created_at')
    .eq('user_id', targetUserId)
    .order('accepted_at', { ascending: false })
    .order('created_at', { ascending: false });

  if (error && isMissingSchemaObjectError(error, 'subscriber_application_documents')) {
    return {
      data: [],
      error: {
        ...error,
        code: 'MISSING_ONBOARDING_DOCS_TABLE',
        message: 'Signed onboarding documents are not available until migration 008_subscriber_onboarding_and_distribution_refresh.sql is applied.',
      },
    };
  }

  return { data: data || [], error };
};

export const acknowledgeSubscriberOnboardingDocuments = async ({
  userId = null,
  signerName,
  signerEmail,
  documents,
  version = DEFAULT_DOCUMENT_VERSION,
  acceptanceMethod = 'electronic_acknowledgment',
}) => {
  const targetUserId = userId || (await getUser())?.id;
  if (!targetUserId) return { error: { message: 'User not found.' }, documents: [] };
  if (!Array.isArray(documents) || documents.length === 0) {
    return { error: { message: 'No onboarding documents were provided.' }, documents: [] };
  }

  const legalConsents = {
    version,
    reviewedAt: new Date().toISOString(),
    acceptedDocuments: documents.map((document) => document.title),
    acceptanceMethod,
    signerName: String(signerName || '').trim() || null,
    signerEmail: String(signerEmail || '').trim() || null,
    documents: documents.map((document) => ({
      slug: document.id,
      title: document.title,
      renderedText: [
        document.summary,
        document.checkboxLabel,
        ...(document.body || []),
      ].filter(Boolean).join('\n\n'),
    })),
  };

  const { error, documents: savedDocuments } = await saveApplicationDocuments(targetUserId, legalConsents);
  if (error) return { error, documents: [] };

  await logSubscriberDelivery({
    userId: targetUserId,
    title: 'Subscriber onboarding documents acknowledged',
    deliveryStatus: 'recorded',
    details: {
      notification_type: 'subscriber_contract_refresh',
      documents_recorded: savedDocuments.length,
      signer_name: legalConsents.signerName,
      signer_email: legalConsents.signerEmail,
    },
  });

  return { error: null, documents: savedDocuments };
};

export const saveSubscriberDepositRecord = async ({
  depositId = null,
  investor,
  amountExpected,
  amountReceived,
  sentAt,
  referenceNumber = null,
  status = 'Received',
  adminNotes = null,
  subscriberNotes,
  memoReceived,
  receivedAt,
  confirmedAt,
}) => {
  if (!investor?.id) return { data: null, error: { message: 'Subscriber account not found.' } };

  const amount = Number(amountExpected);
  if (Number.isNaN(amount) || amount <= 0) {
    return { data: null, error: { message: 'Enter a valid deposit amount.' } };
  }
  if (!SUBSCRIBER_DEPOSIT_STATUSES.includes(status)) {
    return { data: null, error: { message: 'Choose a valid deposit status.' } };
  }

  const manager = await getUser();
  const accountId = formatSubscriberAccountId(investor.subscriberAccountId);
  const subscriberName = investor.name || 'Subscriber';
  const memoExpected = buildZelleMemo({ accountId, subscriberName, amount });
  const normalizedReceived = amountReceived === undefined || amountReceived === null || amountReceived === ''
    ? (['Received', 'Confirmed', 'Active Next Cycle'].includes(status) ? amount : 0)
    : Number(amountReceived);
  const normalizedSentAt = sentAt || new Date().toISOString().slice(0, 10);
  const normalizedReceivedAt = receivedAt !== undefined
    ? receivedAt
    : (['Received', 'Confirmed', 'Active Next Cycle'].includes(status) ? new Date().toISOString() : null);
  const normalizedConfirmedAt = confirmedAt !== undefined
    ? confirmedAt
    : (['Confirmed', 'Active Next Cycle'].includes(status) ? new Date().toISOString() : null);

  const basePayload = {
    subscriber_id: investor.id,
    subscriber_account_id: accountId,
    subscriber_name: subscriberName,
    method: 'Zelle',
    amount_expected: amount,
    amount_received: Number.isFinite(normalizedReceived) ? normalizedReceived : 0,
    memo_expected: memoExpected,
    memo_received: memoReceived !== undefined ? (String(memoReceived || '').trim() || null) : null,
    status,
    sent_at: normalizedSentAt,
    received_at: normalizedReceivedAt,
    confirmed_at: normalizedConfirmedAt,
    reference_number: String(referenceNumber || '').trim() || null,
    admin_notes: adminNotes !== undefined ? (String(adminNotes || '').trim() || null) : null,
    subscriber_notes: subscriberNotes !== undefined ? (String(subscriberNotes || '').trim() || null) : null,
    notion_sync_status: 'pending',
    notion_error: null,
    manager_logged_at: new Date().toISOString(),
    manager_logged_by: manager?.id || null,
  };

  if (depositId) {
    const { data, error } = await supabase
      .from('subscriber_deposits')
      .update(basePayload)
      .eq('id', depositId)
      .select()
      .single();

    if (!error && data?.id) {
      await markNotionMirrorPending({ table: 'subscriber_deposits', recordId: data.id });
      if (['Confirmed', 'Active Next Cycle'].includes(data.status) && Number(data.amount_received || 0) > 0) {
        const fund = investor.fund_id || investor.fundId ? null : await getFundState();
        await postContributionJournal({
          fundId: investor.fund_id || investor.fundId || fund?.id,
          investorId: investor.id,
          amount: Number(data.amount_received),
          sourceTable: 'subscriber_deposits',
          sourceId: data.id,
          referenceId: `deposit:${data.id}`,
          description: `${data.status} subscriber deposit`,
        });
      }
    }

    return { data, error };
  }

  const { data, error } = await supabase
    .from('subscriber_deposits')
    .insert({
      ...basePayload,
      entry_source: 'manager',
    })
    .select()
    .single();

  if (!error && data?.id) {
    await markNotionMirrorPending({ table: 'subscriber_deposits', recordId: data.id });
    if (['Confirmed', 'Active Next Cycle'].includes(data.status) && Number(data.amount_received || 0) > 0) {
      const fund = investor.fund_id || investor.fundId ? null : await getFundState();
      await postContributionJournal({
        fundId: investor.fund_id || investor.fundId || fund?.id,
        investorId: investor.id,
        amount: Number(data.amount_received),
        sourceTable: 'subscriber_deposits',
        sourceId: data.id,
        referenceId: `deposit:${data.id}`,
        description: `${data.status} subscriber deposit`,
      });
    }
  }

  return { data, error };
};

export const submitSubscriberDeposit = async ({
  investor,
  amountSent,
  dateSent,
  referenceNumber = null,
  screenshotFile = null,
  notes = null,
  memoReceived = null,
}) => {
  const amount = Number(amountSent);
  if (!investor?.id) return { data: null, error: { message: 'Subscriber account not found.' } };
  if (Number.isNaN(amount) || amount <= 0) return { data: null, error: { message: 'Enter a valid amount sent.' } };
  const normalizedMemoReceived = String(memoReceived || '').trim();
  if (!normalizedMemoReceived) return { data: null, error: { message: 'Enter the memo used for this transfer.' } };

  const screenshot = await uploadDepositScreenshot({ subscriberId: investor.id, file: screenshotFile });
  if (screenshot.error) return { data: null, error: screenshot.error };

  const accountId = formatSubscriberAccountId(investor.subscriberAccountId);
  const subscriberName = investor.name || 'Subscriber';
  const memoExpected = buildZelleMemo({ accountId, subscriberName, amount });

  const { data, error } = await supabase
    .from('subscriber_deposits')
    .insert({
      subscriber_id: investor.id,
      subscriber_account_id: accountId,
      subscriber_name: subscriberName,
      method: 'Zelle',
      amount_expected: amount,
      amount_received: 0,
      currency: 'USD',
      memo_expected: memoExpected,
      memo_received: normalizedMemoReceived,
      status: 'Sent by Subscriber',
      sent_at: dateSent || new Date().toISOString().slice(0, 10),
      reference_number: String(referenceNumber || '').trim() || null,
      screenshot_url: screenshot.url,
      subscriber_notes: String(notes || '').trim() || null,
    })
    .select()
    .single();

  if (!error && data?.id) {
    await markNotionMirrorPending({ table: 'subscriber_deposits', recordId: data.id });
  }

  return { data, error };
};

export const getAllSubscriberDeposits = async () => {
  const { data, error } = await supabase
    .from('subscriber_deposits')
    .select('*')
    .order('submitted_at', { ascending: false });

  return { data: data || [], error };
};

export const updateSubscriberDepositStatus = async (depositId, {
  status,
  amountReceived,
  memoReceived,
  receivedAt,
  confirmedAt,
  adminNotes,
} = {}) => {
  if (!depositId) return { data: null, error: { message: 'Deposit ID is required.' } };

  const updates = {
    ...(status ? { status } : {}),
    ...(amountReceived !== undefined ? { amount_received: Number(amountReceived) || 0 } : {}),
    ...(memoReceived !== undefined ? { memo_received: String(memoReceived || '').trim() || null } : {}),
    ...(receivedAt !== undefined ? { received_at: receivedAt || null } : {}),
    ...(confirmedAt !== undefined ? { confirmed_at: confirmedAt || null } : {}),
    ...(adminNotes !== undefined ? { admin_notes: String(adminNotes || '').trim() || null } : {}),
    notion_sync_status: 'pending',
    notion_error: null,
  };

  const { data, error } = await supabase
    .from('subscriber_deposits')
    .update(updates)
    .eq('id', depositId)
    .select()
    .single();

  if (!error && data?.id && ['Confirmed', 'Active Next Cycle'].includes(data.status) && Number(data.amount_received || 0) > 0) {
    const fund = await getFundState();
    await postContributionJournal({
      fundId: fund?.id,
      investorId: data.subscriber_id,
      amount: Number(data.amount_received),
      sourceTable: 'subscriber_deposits',
      sourceId: data.id,
      referenceId: `deposit:${data.id}`,
      description: `${data.status} subscriber deposit`,
    });
  }

  return { data, error };
};

export const recordRedemptionInstruction = async ({
  investor,
  distributionChoice,
  zelleName,
  zelleEmail,
  zellePhone,
}) => {
  if (!investor?.id) return { data: null, error: { message: 'Subscriber account not found.' } };
  if (!['payout', 'exit'].includes(distributionChoice)) {
    return { data: null, error: { message: 'Redemption instructions are only required for payout or exit.' } };
  }

  const { profile, error: validationError } = validateZelleProfile({ zelleName, zelleEmail, zellePhone });
  if (validationError) return { data: null, error: validationError };

  const accountId = formatSubscriberAccountId(investor.subscriberAccountId);

  const { data, error } = await supabase
    .from('subscriber_redemption_instructions')
    .insert({
      subscriber_id: investor.id,
      subscriber_account_id: accountId,
      subscriber_name: investor.name || 'Subscriber',
      distribution_choice: distributionChoice,
      method: 'Zelle',
      zelle_name: profile.zelleName,
      zelle_email: profile.zelleEmail || null,
      zelle_phone: profile.zellePhone || null,
      status: 'Confirmed by Subscriber',
    })
    .select()
    .single();

  if (!error && data?.id) {
    await markNotionMirrorPending({ table: 'subscriber_redemption_instructions', recordId: data.id });
  }

  return { data, error };
};

const calculateDistributionBreakdown = ({
  currentNav,
  effectiveHwm,
  totalUnits,
  totalEquity,
  totalInvested,
  capacity,
  investedAmount,
  protectedAllocation = 0,
  unprotectedAllocation = 0,
  protectionStatus = '',
  taxRate = DEFAULT_TAX_RATE,
  grossSubscriberPoolOverride = null,
}) => {
  const hasPoolOverride = grossSubscriberPoolOverride !== null && grossSubscriberPoolOverride !== undefined;
  const grossSubscriberPool = hasPoolOverride
    ? Number(grossSubscriberPoolOverride || 0)
    : ((currentNav - effectiveHwm) * totalUnits) * 0.5;
  const managerCut = hasPoolOverride ? 0 : (currentNav - effectiveHwm) * totalUnits * 0.5;
  const totalProfit = hasPoolOverride ? grossSubscriberPool : (currentNav - effectiveHwm) * totalUnits;
  const poolTaxWithholding = grossSubscriberPool * taxRate;
  const poolPostTaxAmount = grossSubscriberPool - poolTaxWithholding;
  const protectedAmount = Math.max(Number(protectedAllocation || 0), 0);
  const unprotectedAmount = Math.max(Number(unprotectedAllocation || 0), 0);
  const allocationBase = protectedAmount + unprotectedAmount;
  const rawAllocation = capacity > 0 ? allocationBase / capacity : 0;
  const allocationPct = Math.max(rawAllocation, 0);
  const postTaxShare = poolPostTaxAmount * allocationPct;
  const finalNet = postTaxShare;
  const grossPayoutImpact = postTaxShare;
  const postPayoutEquity = totalEquity - grossPayoutImpact;
  const wouldDilutePrincipal = postPayoutEquity < totalInvested;

  return {
    totalProfit,
    managerCut,
    grossSubscriberPool,
    poolTaxWithholding,
    poolPostTaxAmount,
    allocationPct,
    protectedAllocation: protectedAmount,
    unprotectedAllocation: unprotectedAmount,
    protectionStatus,
    postTaxShare,
    protectionAdjustment: 0,
    finalNet,
    grossProfit: postTaxShare,
    fee: 0,
    net: finalNet,
    taxRate,
    grossPayoutImpact,
    postPayoutEquity,
    wouldDilutePrincipal,
  };
};

const deriveProtectionAllocation = ({ activeCapital, fund, investor }) => {
  const capital = Math.max(Number(activeCapital || 0), 0);
  const fundProtectionCapacity = Number(fund?.fund_protection_capacity ?? fund?.fundProtectionCapacity ?? 0);
  const defaultProtectedCap = fundProtectionCapacity > 0 ? fundProtectionCapacity * 0.20 : 0;
  const approved = investor?.approved_protected_amount;
  const hasApproval = approved !== null && approved !== undefined;
  const limit = hasApproval ? Number(approved || 0) : defaultProtectedCap;
  const protectedAllocation = Math.min(capital, Math.max(limit, 0));
  const unprotectedAllocation = Math.max(capital - protectedAllocation, 0);
  return {
    activeCapital: capital,
    fundProtectionCapacity,
    defaultProtectedCap,
    approvedProtectedAmount: hasApproval ? Number(approved || 0) : null,
    protectedAllocation,
    unprotectedAllocation,
    protectionStatus: capital <= 0
      ? 'no_active_capital'
      : hasApproval
        ? 'manually_approved'
        : fundProtectionCapacity <= 0
          ? 'no_capacity'
          : capital > defaultProtectedCap
            ? 'capped'
            : 'within_cap',
  };
};


// ── GET USER ROLE ─────────────────────────────────────────
// Fetches the current user's role from the profiles table.
// Returns 'manager', 'investor', or null if not found.
//
// WHEN TO USE:
//   - ProtectedRoute checks this to enforce role-based access
//   - Components that show different UI based on role
export const getUserRole = async () => {
  const user = await getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', user.id)
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;
  return data.role;
};


// ── GET FUND STATE ────────────────────────────────────────
// Fetches the single fund_state row (there's only one fund).
// Returns the fund object or null if not found.
//
// WHAT'S IN IT:
//   name, base_nav, current_nav, high_water_mark, total_units,
//   manager_units, external_units, max_external_pct, max_single_pct,
//   recovery_mode, total_equity, total_invested, last_updated
export const getFundState = async () => {
  const { data, error } = await supabase
    .from('fund_state')
    .select('*')
    .order('last_updated', { ascending: false, nullsFirst: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;
  const { fundState, status } = await autoCloseExpiredPayoutWindow(data);
  return {
    ...fundState,
    payout_window_expired: status.expired,
    payout_window_expires_at: status.expiresAt,
    payout_window_days_remaining: status.daysRemaining,
  };
};


// ── GET INVESTOR STATE ────────────────────────────────────
// Fetches the investor_state row for a specific user.
// Each investor has exactly one row linked by their user_id.
//
// WHAT'S IN IT:
//   id, user_id, fund_id, units, invested_amount,
//   protection_type, distribution_pref, join_date
//
// RLS NOTE:
//   Investors can only see their own row. Managers can see all.
export const publishFundReportSnapshot = async (snapshot = {}) => {
  const fund = await getFundState();
  if (!fund?.id) return { data: null, error: { message: 'Fund state is unavailable.' } };

  const payload = {
    fund_id: fund.id,
    statement_period: snapshot.statementPeriod || {},
    fund_total_value: Number(snapshot.fundTotalValue || 0),
    gross_realized_result: Number(snapshot.grossRealizedResult || 0),
    realized_series: Array.isArray(snapshot.realizedSeries) ? snapshot.realizedSeries : [],
    yearly_projection: snapshot.yearlyProjection || {},
    projection_disclaimer: snapshot.projectionDisclaimer || 'Projection only. This estimate is based on the previous 30 trading days of activity for the current 52-week fund period and is not a promise of future results.',
    published_at: snapshot.publishedAt || new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('published_fund_reports')
    .insert(payload)
    .select('*')
    .single();

  return { data, error };
};

export const getLatestPublishedFundReport = async () => {
  const { data, error } = await supabase
    .from('published_fund_reports')
    .select('*')
    .order('published_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return { data: null, error };

  return {
    data: {
      id: data.id,
      fundTotalValue: Number(data.fund_total_value || 0),
      grossRealizedResult: Number(data.gross_realized_result || 0),
      realizedSeries: Array.isArray(data.realized_series) ? data.realized_series : [],
      yearlyProjection: data.yearly_projection || {},
      statementPeriod: data.statement_period || {},
      publishedAt: data.published_at,
      projectionDisclaimer: data.projection_disclaimer || '',
    },
    error: null,
  };
};

export const getInvestorState = async (userId) => {
  const { data, error } = await supabase
    .from('investor_state')
    .select('*')
    .eq('user_id', userId)
    .order('removed_at', { ascending: true, nullsFirst: true })
    .order('join_date', { ascending: false, nullsFirst: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;

  if (!data.subscriber_account_id) {
    const generatedId = buildLocalSubscriberAccountId(data.id || userId);
    const { data: repaired, error: repairError } = await supabase
      .from('investor_state')
      .update({ subscriber_account_id: generatedId })
      .eq('id', data.id)
      .is('subscriber_account_id', null)
      .select('*')
      .maybeSingle();

    if (!repairError && repaired?.subscriber_account_id) {
      return repaired;
    }

    return { ...data, subscriber_account_id: generatedId };
  }

  return data;
};


// ── GET NAV HISTORY ───────────────────────────────────────
// Fetches all NAV snapshots for a fund, ordered oldest → newest.
// Used to draw the NAV performance sparkline chart.
//
// Returns an array of { date, nav } objects.
export const getFundValueSettlements = async (fundId) => {
  const { data, error } = await supabase
    .from('fund_value_settlements')
    .select('*')
    .eq('fund_id', fundId)
    .order('valuation_date', { ascending: true })
    .order('created_at', { ascending: true });

  if (error || !data) return [];
  return data;
};

export const getFundValueHistory = async (fundId) => (
  (await getFundValueSettlements(fundId)).map((row) => ({
    date: row.valuation_date,
    fundValue: Number(row.post_payout_value ?? row.gross_fund_value ?? 0),
    grossFundValue: Number(row.gross_fund_value ?? 0),
    payoutPool: Number(row.payout_pool ?? 0),
    created_at: row.created_at,
  }))
);

export const getNavHistory = async (fundId) => (
  (await getFundValueHistory(fundId)).map((row) => ({
    date: row.date,
    nav: row.fundValue,
    fundValue: row.fundValue,
    created_at: row.created_at,
  }))
);

export const getNavSettlements = async (fundId) => getFundValueSettlements(fundId);

export const getLatestUnprocessedValuePayout = async (fundId) => {
  if (!fundId) return null;

  const { data, error } = await supabase
    .from('fund_value_settlements')
    .select('*')
    .eq('fund_id', fundId)
    .gt('payout_pool', 0)
    .is('distribution_processed_at', null)
    .order('valuation_date', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;
  return data;
};

export const getLatestUnprocessedSettlementPayout = async (fundId) => getLatestUnprocessedValuePayout(fundId);

export const markValueDistributionProcessed = async ({ settlementId, period }) => {
  if (!settlementId) return { error: null };

  const { error } = await supabase
    .from('fund_value_settlements')
    .update({
      distribution_processed_at: new Date().toISOString(),
      distribution_period: period || null,
    })
    .eq('id', settlementId);

  return { error };
};

export const markSettlementDistributionProcessed = markValueDistributionProcessed;


// ── GET MY DISTRIBUTIONS ──────────────────────────────────
// Fetches distribution records for a single investor.
// Used in the investor portal's distribution history table.
//
// Returns an array of distribution objects, newest first.
export const getMyDistributions = async (investorId) => {
  const { data, error } = await supabase
    .from('distributions')
    .select('*')
    .eq('investor_id', investorId)
    .order('created_at', { ascending: true });

  if (error || !data) return [];
  return data;
};


// ── GET ALL DISTRIBUTIONS (MANAGER) ──────────────────────
// Fetches ALL distribution records for a fund (all investors).
// Used in the manager dashboard's distribution history table.
//
// RLS ensures only managers get results here.
export const getAllDistributions = async (fundId) => {
  const { data, error } = await supabase
    .from('distributions')
    .select('*')
    .eq('fund_id', fundId)
    .order('created_at', { ascending: true });

  if (error || !data) return [];
  return data;
};

export const getSubscriberCapitalLedger = async (investorId) => {
  const { data, error } = await supabase
    .from('subscriber_capital_ledger')
    .select('*')
    .eq('investor_id', investorId)
    .order('event_date', { ascending: true })
    .order('created_at', { ascending: true });

  if (error || !data) return [];
  return data;
};

export const recordSubscriberCapitalEvent = async ({
  fundId,
  investorId,
  eventType,
  amount,
  eventDate = null,
  description = '',
  sourceTable = null,
  sourceId = null,
  referenceId = null,
  importBatchId = null,
  importEventId = null,
  metadata = {},
}) => {
  const normalizedAmount = Number(amount || 0);
  if (!fundId || !investorId || !eventType || !Number.isFinite(normalizedAmount)) {
    return { data: null, error: { message: 'Capital ledger event is missing required fields.' } };
  }

  const payload = {
    fund_id: fundId,
    investor_id: investorId,
    event_type: eventType,
    event_date: eventDate || new Date().toISOString().slice(0, 10),
    amount: normalizedAmount,
    description: String(description || '').trim() || null,
    source_table: sourceTable,
    source_id: sourceId,
    reference_id: referenceId,
    import_batch_id: importBatchId,
    import_event_id: importEventId,
    metadata,
  };

  const { data, error } = await supabase
    .from('subscriber_capital_ledger')
    .insert(payload)
    .select('*')
    .single();

  return { data, error };
};


// ── GET ALL INVESTORS (MANAGER) ───────────────────────────
// Fetches all investor_state rows for a fund.
// Used in the manager dashboard's investor table.
//
// Also joins with profiles to get the investor's username.
// RLS ensures only managers get results here.
export const getAllInvestors = async (fundId) => {
  // Step 1: Fetch all investor_state rows for this fund
  // NOTE: We do NOT join profiles here because investor_state.user_id
  //       references auth.users, not profiles. Supabase can't resolve
  //       that cross-schema join. Instead, we fetch profiles separately.
  const { data, error } = await supabase
    .from('investor_state')
    .select(`
      id,
      user_id,
      invested_amount,
      protection_type,
      protection_locked,
      distribution_pref,
      subscriber_account_id,
      join_date,
      summary_contributed_amount_override,
      summary_current_value_override,
      summary_cash_received_override,
      summary_override_reason,
      summary_override_updated_at,
      summary_override_updated_by,
      approved_protected_amount,
      protection_approval_reason,
      protection_approved_by,
      protection_approved_at,
      current_balance,
      removed_at,
      removal_reason,
      removed_by,
      pending_auth_delete
    `)
    .eq('fund_id', fundId)
    .order('join_date', { ascending: true });

  if (error || !data) return [];

  // Step 2: Fetch profile usernames for all investor user_ids
  // This is a separate query because profiles and investor_state
  // don't have a direct foreign key relationship.
  const userIds = data.map(inv => inv.user_id);
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, username, status, phone, zelle_name, zelle_email, zelle_phone, zelle_updated_at')
    .in('id', userIds);

  // Build a quick lookup map: userId → username
  const profileMap = {};
  (profiles || []).forEach((p) => {
    profileMap[p.id] = {
      username: p.username,
      status: p.status || 'approved',
      phone: p.phone || '',
      zelleName: p.zelle_name || '',
      zelleEmail: p.zelle_email || '',
      zellePhone: p.zelle_phone || '',
      zelleUpdatedAt: p.zelle_updated_at || null,
    };
  });

  // Step 3: Attach the profile username to each investor
  return data.map(inv => ({
    ...inv,
    profiles: {
      username: resolveProfileDisplayName(profileMap[inv.user_id]?.username, 'Subscriber'),
      status: profileMap[inv.user_id]?.status || 'approved',
      phone: profileMap[inv.user_id]?.phone || '',
      zelleName: profileMap[inv.user_id]?.zelleName || '',
      zelleEmail: profileMap[inv.user_id]?.zelleEmail || '',
      zellePhone: profileMap[inv.user_id]?.zellePhone || '',
      zelleUpdatedAt: profileMap[inv.user_id]?.zelleUpdatedAt || null,
    },
  }));
};

export const getSubscriberValuationSnapshots = async (fundId, userId = null) => {
  if (!fundId) return { data: [], error: null };

  const { data, error } = await supabase.rpc('get_subscriber_valuation_snapshots', {
    p_fund_id: fundId,
    p_user_id: userId,
  });

  if (error) return { data: [], error };
  return { data: data || [], error: null };
};

const buildProtectionAllocationFallback = ({ investors = [], fund, valuationRows = [] }) => {
  const valuationMap = {};
  (valuationRows || []).forEach((row) => {
    valuationMap[row.investor_id] = row;
  });
  const capacity = Number(fund?.fund_protection_capacity ?? fund?.fundProtectionCapacity ?? 0);
  const defaultCap = capacity > 0 ? capacity * 0.20 : 0;

  return (investors || []).map((investor) => {
    const valuation = valuationMap[investor.id] || {};
    const activeCapital = Math.max(Number(
      valuation.current_value
      ?? investor.summary_current_value_override
      ?? investor.value_override_amount
      ?? investor.current_balance
      ?? investor.invested_amount
      ?? 0
    ), 0);
    const approvedAmount = investor.approved_protected_amount ?? null;
    const protectedLimit = approvedAmount !== null && approvedAmount !== undefined
      ? Number(approvedAmount || 0)
      : defaultCap;
    const protectedAllocation = Math.min(activeCapital, Math.max(protectedLimit, 0));
    const unprotectedAllocation = Math.max(activeCapital - protectedAllocation, 0);

    return {
      investor_id: investor.id,
      user_id: investor.user_id,
      active_capital: activeCapital,
      fund_protection_capacity: capacity,
      default_protected_cap: defaultCap,
      approved_protected_amount: approvedAmount,
      protected_allocation: protectedAllocation,
      unprotected_allocation: unprotectedAllocation,
      protection_status: activeCapital <= 0
        ? 'no_active_capital'
        : approvedAmount !== null && approvedAmount !== undefined
          ? 'manually_approved'
          : capacity <= 0
            ? 'no_capacity'
            : activeCapital > defaultCap
              ? 'capped'
              : 'within_cap',
      protection_approval_reason: investor.protection_approval_reason || '',
      protection_approved_by: investor.protection_approved_by || null,
      protection_approved_at: investor.protection_approved_at || null,
    };
  });
};

export const getSubscriberProtectionAllocations = async (fundId, userId = null, fallbackContext = null) => {
  if (!fundId) return { data: [], error: null };

  const { data, error } = await supabase.rpc('get_subscriber_protection_allocations', {
    p_fund_id: fundId,
    p_user_id: userId,
  });

  if (!error) return { data: data || [], error: null };
  if (fallbackContext) {
    return {
      data: buildProtectionAllocationFallback(fallbackContext),
      error,
    };
  }
  return { data: [], error };
};

export const updateSubscriberValueOverride = async ({
  investorId,
  fundId,
  overrideAmount,
  effectiveAt,
  reason,
}) => {
  const trimmedReason = String(reason || '').trim();
  if (!trimmedReason) {
    return { error: { message: 'A reason is required for subscriber value corrections.' } };
  }

  const normalizedAmount = overrideAmount === null || overrideAmount === '' ? null : Number(overrideAmount);
  if (normalizedAmount !== null && (!Number.isFinite(normalizedAmount) || normalizedAmount < 0)) {
    return { error: { message: 'Override value must be blank or zero or greater.' } };
  }

  const normalizedEffectiveAt = effectiveAt
    ? (String(effectiveAt).includes('T') ? effectiveAt : `${effectiveAt}T12:00:00.000Z`)
    : null;

  const { data: current, error: currentError } = await supabase
    .from('investor_state')
    .select('value_override_amount, value_override_effective_at, value_override_reason, user_id')
    .eq('id', investorId)
    .single();

  if (currentError || !current) {
    return { error: currentError || { message: 'Subscriber not found.' } };
  }

  const payload = {
    value_override_amount: normalizedAmount,
    value_override_effective_at: normalizedAmount === null ? null : normalizedEffectiveAt,
    value_override_reason: normalizedAmount === null ? null : trimmedReason,
    value_override_updated_at: new Date().toISOString(),
  };

  const { error: updateError } = await supabase
    .from('investor_state')
    .update(payload)
    .eq('id', investorId);

  if (updateError) return { error: updateError };

  await logFundAudit({
    fundId,
    investorId,
    entityType: 'investor_state',
    entityId: investorId,
    actionType: normalizedAmount === null ? 'value_override_cleared' : 'value_override_updated',
    fieldName: 'value_override_amount',
    oldValue: {
      amount: current.value_override_amount,
      effectiveAt: current.value_override_effective_at,
      reason: current.value_override_reason,
    },
    newValue: {
      amount: normalizedAmount,
      effectiveAt: payload.value_override_effective_at,
      reason: payload.value_override_reason,
    },
    reason: trimmedReason,
    metadata: { user_id: current.user_id },
  });

  return { error: null };
};

export const updateSubscriberSummaryOverrides = async ({
  investorId,
  fundId,
  amountContributed,
  joinDate,
  currentValue,
  cashReceived,
  totalAccountValue,
  totalReturn,
  reason,
}) => {
  const trimmedReason = String(reason || '').trim();
  if (!trimmedReason) {
    return { error: { message: 'A reason is required for subscriber summary edits.' } };
  }

  const normalizeMoney = (value, fieldLabel) => {
    if (value === null || value === undefined || value === '') {
      return { value: null, error: { message: `${fieldLabel} is required.` } };
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return { value: null, error: { message: `${fieldLabel} must be zero or greater.` } };
    }
    return { value: parsed, error: null };
  };

  const contributed = normalizeMoney(amountContributed, 'Amount contributed');
  if (contributed.error) return { error: contributed.error };
  const current = normalizeMoney(currentValue, 'Current value');
  if (current.error) return { error: current.error };
  const cash = normalizeMoney(cashReceived, 'Cash received');
  if (cash.error) return { error: cash.error };

  const normalizedJoinDate = String(joinDate || '').trim() || null;
  if (!normalizedJoinDate) {
    return { error: { message: 'Time in fund / join date is required.' } };
  }

  const recalculatedTotalAccountValue = current.value + cash.value;
  const recalculatedTotalReturn = recalculatedTotalAccountValue - contributed.value;
  const recalculatedTotalReturnPct = contributed.value > 0
    ? (recalculatedTotalReturn / contributed.value) * 100
    : 0;

  const typedTotalAccountValue = totalAccountValue === '' || totalAccountValue === null || totalAccountValue === undefined
    ? null
    : Number(totalAccountValue);
  const typedTotalReturn = totalReturn === '' || totalReturn === null || totalReturn === undefined
    ? null
    : Number(totalReturn);

  const { data: currentRow, error: currentError } = await supabase
    .from('investor_state')
    .select(`
      user_id,
      join_date,
      invested_amount,
      summary_contributed_amount_override,
      summary_current_value_override,
      summary_cash_received_override,
      summary_override_reason
    `)
    .eq('id', investorId)
    .single();

  if (currentError || !currentRow) {
    return { error: currentError || { message: 'Subscriber not found.' } };
  }

  const currentUser = await getUser();
  const payload = {
    join_date: normalizedJoinDate,
    summary_contributed_amount_override: contributed.value,
    summary_current_value_override: current.value,
    summary_cash_received_override: cash.value,
    summary_override_reason: trimmedReason,
    summary_override_updated_at: new Date().toISOString(),
    summary_override_updated_by: currentUser?.id || null,
  };

  const { error: updateError } = await supabase
    .from('investor_state')
    .update(payload)
    .eq('id', investorId);

  if (updateError) return { error: updateError };

  await logFundAudit({
    fundId,
    investorId,
    entityType: 'investor_state',
    entityId: investorId,
    actionType: 'summary_override_updated',
    fieldName: 'subscriber_summary',
    oldValue: {
      joinDate: currentRow.join_date,
      amountContributed: currentRow.summary_contributed_amount_override ?? currentRow.invested_amount,
      currentValue: currentRow.summary_current_value_override,
      cashReceived: currentRow.summary_cash_received_override,
      reason: currentRow.summary_override_reason,
    },
    newValue: {
      joinDate: normalizedJoinDate,
      amountContributed: contributed.value,
      currentValue: current.value,
      cashReceived: cash.value,
      totalAccountValue: recalculatedTotalAccountValue,
      totalReturn: recalculatedTotalReturn,
      totalReturnPct: recalculatedTotalReturnPct,
      typedTotalAccountValue,
      typedTotalReturn,
      reason: trimmedReason,
    },
    reason: trimmedReason,
    metadata: {
      user_id: currentRow.user_id,
      recalculated_total_account_value: recalculatedTotalAccountValue,
      recalculated_total_return: recalculatedTotalReturn,
      recalculated_total_return_pct: recalculatedTotalReturnPct,
      typed_total_account_value: typedTotalAccountValue,
      typed_total_return: typedTotalReturn,
    },
  });

  return {
    error: null,
    data: {
      amountContributed: contributed.value,
      joinDate: normalizedJoinDate,
      currentValue: current.value,
      cashReceived: cash.value,
      totalAccountValue: recalculatedTotalAccountValue,
      totalReturn: recalculatedTotalReturn,
      totalReturnPct: recalculatedTotalReturnPct,
    },
  };
};

export const getProfileById = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, status, phone, zelle_name, zelle_email, zelle_phone, zelle_updated_at')
    .eq('id', userId)
    .single();

  if (error || !data) return { data: null, error };

  return {
    data: {
      ...data,
      username: resolveProfileDisplayName(data.username, ''),
      hasPlaceholderName: isPlaceholderProfileName(data.username),
    },
    error: null,
  };
};

const logFundAudit = async ({
  fundId = null,
  investorId = null,
  distributionId = null,
  navDate = null,
  entityType,
  entityId,
  actionType,
  fieldName = null,
  oldValue = null,
  newValue = null,
  reason = null,
  metadata = {},
}) => {
  const user = await getUser();

  const { error } = await supabase
    .from('fund_audit_log')
    .insert({
      fund_id: fundId,
      investor_id: investorId,
      distribution_id: distributionId,
      nav_date: navDate,
      entity_type: entityType,
      entity_id: String(entityId),
      action_type: actionType,
      field_name: fieldName,
      old_value: oldValue ?? {},
      new_value: newValue ?? {},
      reason: reason ? String(reason).trim() : null,
      metadata,
      edited_by: user?.id || null,
    });

  if (error) {
    return {
      error: {
        ...error,
        message: error.message || 'The fund change was saved, but the audit log entry could not be written.',
      },
    };
  }

  return { error: null };
};

const syncFundSnapshotFromHistory = async (fundId) => {
  const fund = await getFundState();
  if (!fund) return { error: { message: 'Fund not found' } };

  const history = await getNavHistory(fundId);
  if (!history.length) {
    const { error } = await supabase
      .from('fund_state')
      .update({
        base_nav: 0,
        current_nav: 0,
        high_water_mark: 0,
        recovery_mode: false,
        total_equity: 0,
        last_updated: new Date().toISOString(),
      })
      .eq('id', fundId);

    return { error };
  }

  const sorted = [...history].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const baseNav = Number(sorted[0]?.nav || 0);
  const currentNav = Number(sorted[sorted.length - 1]?.nav || 0);
  const highWaterMark = sorted.reduce((max, row) => Math.max(max, Number(row.nav || 0)), 0);
  const totalUnits = Number(fund.total_units || 0);

  const { error } = await supabase
    .from('fund_state')
    .update({
      base_nav: baseNav,
      current_nav: currentNav,
      high_water_mark: highWaterMark,
      recovery_mode: currentNav <= highWaterMark && highWaterMark > 0,
      total_equity: totalUnits * currentNav,
      last_updated: new Date().toISOString(),
    })
    .eq('id', fundId);

  return { error };
};

export const getInvestorDistributions = async (investorId) => {
  const { data, error } = await supabase
    .from('distributions')
    .select('*')
    .eq('investor_id', investorId)
    .order('created_at', { ascending: false });

  if (error || !data) return [];
  return data;
};

export const getFundAuditLogs = async ({ fundId, investorId = null, limit = 50 } = {}) => {
  let query = supabase
    .from('fund_audit_log')
    .select('*')
    .order('edited_at', { ascending: false })
    .limit(limit);

  if (fundId) query = query.eq('fund_id', fundId);
  if (investorId) query = query.eq('investor_id', investorId);

  const { data, error } = await query;
  if (error || !data) return [];
  return data;
};

export const updateSubscriberRecord = async ({
  investorId,
  fundId,
  investedAmount,
  joinDate,
  reason,
}) => {
  const normalizedReason = String(reason || '').trim();
  if (!normalizedReason) {
    return { error: { message: 'A reason is required for subscriber edits.' } };
  }

  const { data: current, error: currentError } = await supabase
    .from('investor_state')
    .select('id, user_id, fund_id, invested_amount, join_date')
    .eq('id', investorId)
    .single();

  if (currentError || !current) {
    return { error: currentError || { message: 'Subscriber not found.' } };
  }

  const nextInvested = Number(investedAmount);
  if (Number.isNaN(nextInvested) || nextInvested < 0) {
    return { error: { message: 'Subscribed amount must be zero or greater.' } };
  }

  const nextJoinDate = joinDate || null;
  const investedDelta = nextInvested - Number(current.invested_amount || 0);

  const { error: investorError } = await supabase
    .from('investor_state')
    .update({
      invested_amount: nextInvested,
      join_date: nextJoinDate,
    })
    .eq('id', investorId);

  if (investorError) return { error: investorError };

  const fund = await getFundState();
  if (!fund) return { error: { message: 'Fund not found.' } };

  const updatedInvested = Math.max(Number(fund.total_invested || 0) + investedDelta, 0);
  const updatedEquity = Math.max(Number(fund.total_equity || 0) + investedDelta, 0);

  const { error: fundError } = await supabase
    .from('fund_state')
    .update({
      total_invested: updatedInvested,
      total_equity: updatedEquity,
      last_updated: new Date().toISOString(),
    })
    .eq('id', fundId);

  if (fundError) return { error: fundError };

  if (investedDelta !== 0) {
    await recordSubscriberCapitalEvent({
      fundId,
      investorId,
      eventType: 'adjustment',
      amount: investedDelta,
      eventDate: nextJoinDate || undefined,
      description: `Manager subscriber edit: ${normalizedReason}`,
      sourceTable: 'investor_state',
      sourceId: investorId,
      referenceId: `subscriber-edit:${investorId}:${Date.now()}`,
      metadata: {
        previous_invested_amount: Number(current.invested_amount || 0),
        next_invested_amount: nextInvested,
      },
    });
  }

  const changes = [
    ['invested_amount', Number(current.invested_amount || 0), nextInvested],
    ['join_date', current.join_date || null, nextJoinDate],
  ].filter(([, oldVal, newVal]) => String(oldVal ?? '') !== String(newVal ?? ''));

  for (const [fieldName, oldVal, newVal] of changes) {
    await logFundAudit({
      fundId,
      investorId,
      entityType: 'investor_state',
      entityId: investorId,
      actionType: 'update',
      fieldName,
      oldValue: { value: oldVal },
      newValue: { value: newVal },
      reason: normalizedReason,
      metadata: { user_id: current.user_id },
    });
  }

  return { error: null };
};

export const updateDistributionRecord = async ({
  distributionId,
  reason,
  changes,
}) => {
  const normalizedReason = String(reason || '').trim();
  if (!normalizedReason) {
    return { error: { message: 'A reason is required for distribution edits.' } };
  }

  const { data: current, error: currentError } = await supabase
    .from('distributions')
    .select('*')
    .eq('id', distributionId)
    .single();

  if (currentError || !current) {
    return { error: currentError || { message: 'Distribution not found.' } };
  }

  const nextValues = {
    period: changes.period ?? current.period,
    dist_type: changes.dist_type ?? current.dist_type,
    gross_profit: changes.gross_profit ?? current.gross_profit,
    fee: changes.fee ?? current.fee,
    net: changes.net ?? current.net,
    manual_adjustment: changes.manual_adjustment ?? current.manual_adjustment ?? 0,
    created_at: changes.created_at ?? current.created_at,
  };

  const { error: updateError } = await supabase
    .from('distributions')
    .update(nextValues)
    .eq('id', distributionId);

  if (updateError) return { error: updateError };

  const netDelta = Number(nextValues.net || 0) - Number(current.net || 0);
  if (nextValues.dist_type === 'reinvest' && netDelta !== 0) {
    await recordSubscriberCapitalEvent({
      fundId: current.fund_id,
      investorId: current.investor_id,
      eventType: 'adjustment',
      amount: netDelta,
      eventDate: nextValues.created_at ? String(nextValues.created_at).slice(0, 10) : undefined,
      description: `Distribution history edit: ${normalizedReason}`,
      sourceTable: 'distributions',
      sourceId: distributionId,
      referenceId: `distribution-edit:${distributionId}:${Date.now()}`,
      metadata: { previous_net: current.net, next_net: nextValues.net },
    });
  }

  for (const fieldName of Object.keys(nextValues)) {
    if (String(current[fieldName] ?? '') === String(nextValues[fieldName] ?? '')) continue;

    await logFundAudit({
      fundId: current.fund_id,
      investorId: current.investor_id,
      distributionId,
      entityType: 'distributions',
      entityId: distributionId,
      actionType: 'update',
      fieldName,
      oldValue: { value: current[fieldName] },
      newValue: { value: nextValues[fieldName] },
      reason: normalizedReason,
    });
  }

  return { error: null };
};

export const createDistributionRecord = async ({
  fundId,
  investorId,
  reason,
  values,
}) => {
  const normalizedReason = String(reason || '').trim();
  if (!normalizedReason) {
    return { error: { message: 'A reason is required for distribution entries.' } };
  }

  const nextValues = {
    period: values.period ?? '',
    dist_type: values.dist_type ?? 'payout',
    gross_profit: Number(values.gross_profit || 0),
    fee: Number(values.fee || 0),
    net: Number(values.net || 0),
    manual_adjustment: Number(values.manual_adjustment || 0),
    created_at: values.created_at ?? new Date().toISOString(),
  };

  const { data: inserted, error: insertError } = await supabase
    .from('distributions')
    .insert({
      fund_id: fundId,
      investor_id: investorId,
      period: nextValues.period,
      dist_type: nextValues.dist_type,
      gross_profit: nextValues.gross_profit,
      fee: nextValues.fee,
      net: nextValues.net,
      manual_adjustment: nextValues.manual_adjustment,
      created_at: nextValues.created_at,
    })
    .select('id')
    .single();

  if (insertError || !inserted) {
    return { error: insertError || { message: 'Could not create distribution record.' } };
  }

  if (nextValues.dist_type === 'reinvest' && nextValues.net !== 0) {
    await recordSubscriberCapitalEvent({
      fundId,
      investorId,
      eventType: 'reinvestment',
      amount: nextValues.net,
      eventDate: String(nextValues.created_at).slice(0, 10),
      description: `Distribution history import: ${normalizedReason}`,
      sourceTable: 'distributions',
      sourceId: inserted.id,
      referenceId: `distribution-reinvestment:${inserted.id}`,
      metadata: { period: nextValues.period },
    });
  }

  const { error: auditError } = await logFundAudit({
    fundId,
    investorId,
    distributionId: inserted.id,
    entityType: 'distributions',
    entityId: inserted.id,
    actionType: 'create',
    fieldName: 'distribution_record',
    oldValue: { value: null },
    newValue: {
      value: {
        period: nextValues.period,
        dist_type: nextValues.dist_type,
        gross_profit: nextValues.gross_profit,
        fee: nextValues.fee,
        net: nextValues.net,
        created_at: nextValues.created_at,
      },
    },
    reason: normalizedReason,
  });

  return { error: null, warning: auditError?.message || null };
};

export const updateNavHistoryRecord = async ({
  fundId,
  date,
  nav,
  reason,
}) => {
  const normalizedReason = String(reason || '').trim();
  if (!normalizedReason) {
    return { error: { message: 'A reason is required for NAV history edits.' } };
  }

  const nextNav = Number(nav);
  if (Number.isNaN(nextNav) || nextNav < 0) {
    return { error: { message: 'NAV must be zero or greater.' } };
  }

  const { data: existing } = await supabase
    .from('nav_history')
    .select('date, nav')
    .eq('fund_id', fundId)
    .eq('date', date)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('nav_history')
      .update({ nav: nextNav })
      .eq('fund_id', fundId)
      .eq('date', date);

    if (error) return { error };
  } else {
    const { error } = await supabase
      .from('nav_history')
      .insert({ fund_id: fundId, date, nav: nextNav });

    if (error) return { error };
  }

  const { error: syncError } = await syncFundSnapshotFromHistory(fundId);
  if (syncError) return { error: syncError };

  const { error: auditError } = await logFundAudit({
    fundId,
    navDate: date,
    entityType: 'nav_history',
    entityId: `${fundId}:${date}`,
    actionType: existing ? 'update' : 'insert',
    fieldName: 'nav',
    oldValue: { value: existing?.nav ?? null },
    newValue: { value: nextNav },
    reason: normalizedReason,
  });

  return { error: null, warning: auditError?.message || null };
};

export const deleteNavHistoryRecord = async ({
  fundId,
  date,
  reason,
}) => {
  const normalizedReason = String(reason || '').trim();
  if (!normalizedReason) {
    return { error: { message: 'A reason is required for NAV history deletions.' } };
  }

  const { data: existing } = await supabase
    .from('nav_history')
    .select('date, nav')
    .eq('fund_id', fundId)
    .eq('date', date)
    .maybeSingle();

  if (!existing) {
    return { error: { message: 'NAV history row not found.' } };
  }

  const { error: deleteError } = await supabase
    .from('nav_history')
    .delete()
    .eq('fund_id', fundId)
    .eq('date', date);

  if (deleteError) return { error: deleteError };

  const { error: syncError } = await syncFundSnapshotFromHistory(fundId);
  if (syncError) return { error: syncError };

  const { error: auditError } = await logFundAudit({
    fundId,
    navDate: date,
    entityType: 'nav_history',
    entityId: `${fundId}:${date}`,
    actionType: 'delete',
    fieldName: 'nav',
    oldValue: { value: existing.nav },
    newValue: { value: null },
    reason: normalizedReason,
  });

  return { error: null, warning: auditError?.message || null };
};

export const deleteNavHistoryRecords = async ({
  fundId,
  dates,
  reason,
}) => {
  const normalizedReason = String(reason || '').trim();
  if (!normalizedReason) {
    return { error: { message: 'A reason is required for NAV history deletions.' } };
  }

  const uniqueDates = [...new Set((dates || []).filter(Boolean).map((value) => String(value)))];
  if (!uniqueDates.length) {
    return { error: { message: 'Select at least one NAV history date to delete.' } };
  }

  const { data: existingRows, error: fetchError } = await supabase
    .from('nav_history')
    .select('date, nav')
    .eq('fund_id', fundId)
    .in('date', uniqueDates);

  if (fetchError) return { error: fetchError };
  if (!existingRows?.length) {
    return { error: { message: 'Selected NAV history rows were not found.' } };
  }

  const { error: deleteError } = await supabase
    .from('nav_history')
    .delete()
    .eq('fund_id', fundId)
    .in('date', uniqueDates);

  if (deleteError) return { error: deleteError };

  const { error: syncError } = await syncFundSnapshotFromHistory(fundId);
  if (syncError) return { error: syncError };

  const auditWarnings = [];
  for (const row of existingRows) {
    const { error: auditError } = await logFundAudit({
      fundId,
      navDate: row.date,
      entityType: 'nav_history',
      entityId: `${fundId}:${row.date}`,
      actionType: 'delete',
      fieldName: 'nav',
      oldValue: { value: row.nav },
      newValue: { value: null },
      reason: normalizedReason,
      metadata: { bulk_delete: true, deleted_count: existingRows.length },
    });

    if (auditError?.message) {
      auditWarnings.push(`${row.date}: ${auditError.message}`);
    }
  }

  return {
    error: null,
    deletedDates: existingRows.map((row) => row.date),
    deletedCount: existingRows.length,
    warning: auditWarnings.length
      ? `NAV history was deleted, but ${auditWarnings.length} audit ${auditWarnings.length === 1 ? 'entry failed' : 'entries failed'} to write.`
      : null,
  };
};


// ════════════════════════════════════════════════════════════
// MANAGER WRITE OPERATIONS
// ════════════════════════════════════════════════════════════
// These functions modify data. RLS ensures only managers can
// call them successfully. If an investor tries, Supabase will
// return an error or empty result.


// ── UPDATE NAV ────────────────────────────────────────────
// Updates the fund's current NAV and inserts a nav_history snapshot.
// Also updates high_water_mark and recovery_mode if needed.
//
// WHEN TO USE:
//   Manager enters a new NAV value daily in Phase3Dashboard.
//
// WHAT IT DOES:
//   1. Updates fund_state.current_nav
//   2. If new NAV > HWM, updates high_water_mark
//   3. Sets recovery_mode based on NAV vs HWM
//   4. Inserts a new nav_history row for today
//   5. Updates total_equity = total_units × new NAV
export const updateNav = async (fundId, newNav) => {
  // Step 1: Get current fund state to check HWM
  const fund = await getFundState();
  if (!fund) return { error: { message: 'Fund not found' } };

  // Step 2: Calculate recovery mode
  // HWM does NOT change during daily NAV updates.
  // The watermark stays frozen at whatever recordDistribution() set it to.
  // This way, any NAV growth above the watermark = distributable profit.
  //
  // Recovery mode = NAV is still below the watermark (no distributions allowed).
  const currentHwm = Number(fund.high_water_mark);
  const newRecovery = newNav <= currentHwm;
  const newEquity = Number(fund.total_units) * newNav;

  // Step 2b: Check if this is the first NAV update
  // If nav_history is empty, this is the initial CSV upload.
  // Set base_nav to this first real NAV instead of the seed value ($10).
  // Also set HWM to match so the fund starts clean (no phantom profit).
  const { count: historyCount } = await supabase
    .from('nav_history')
    .select('id', { count: 'exact', head: true })
    .eq('fund_id', fundId);

  const isFirstNav = historyCount === 0;

  // Step 3: Update the fund_state row (HWM stays unchanged unless first NAV)
  const updatePayload = {
    current_nav:     newNav,
    recovery_mode:   newRecovery,
    total_equity:    newEquity,
    last_updated:    new Date().toISOString(),
  };
  // First NAV upload → set base_nav and HWM to the real starting value
  if (isFirstNav) {
    updatePayload.base_nav        = newNav;
    updatePayload.high_water_mark = newNav;
  }
  const { error: updateError } = await supabase
    .from('fund_state')
    .update(updatePayload)
    .eq('id', fundId);

  if (updateError) return { error: updateError };

  // Step 4: Insert a nav_history snapshot for today
  // Uses UPSERT so if you update NAV twice in one day, it overwrites
  const { error: historyError } = await supabase
    .from('nav_history')
    .upsert(
      { fund_id: fundId, date: new Date().toISOString().split('T')[0], nav: newNav },
      { onConflict: 'fund_id,date' }
    );

  if (historyError) return { error: historyError };

  return { error: null };
};


// ── INVITE INVESTOR ───────────────────────────────────────
// Creates a new investor account and their investor_state row.
//
// HOW IT WORKS:
//   1. Uses Supabase's inviteUserByEmail() to send an invite email
//   2. The investor clicks the link and sets their password
//   3. The existing profiles trigger auto-creates their profile row
//   4. We create their investor_state row here
//
// NOTE: inviteUserByEmail() requires the service_role key on the
// server side. If this fails with a permissions error, you'll need
// to set up a Supabase Edge Function for invites. For now, we can
// create the investor_state row after the user signs up manually.
//
// PARAMETERS:
//   email           — investor's email address
//   name            — display name (stored in profiles.username)
//   units           — initial units to issue
//   investedAmount  — cash amount invested
//   protectionType  — 'full_protection' or 'none'
//   fundId          — which fund to add them to
export const createFundValueSettlement = async ({
  fundId,
  valuationDate,
  grossFundValue,
  payoutAmount = 0,
  managerNote,
}) => {
  const fund = await getFundState();
  if (!fund) return { data: null, error: { message: 'Fund not found' } };

  const gross = Number(grossFundValue);
  const payout = Number(payoutAmount || 0);
  const note = String(managerNote || '').trim();
  const date = valuationDate || new Date().toISOString().split('T')[0];

  if (!date) return { data: null, error: { message: 'Enter a valuation date.' } };
  if (!Number.isFinite(gross) || gross <= 0) {
    return { data: null, error: { message: 'Gross fund value must be greater than $0.' } };
  }
  if (!Number.isFinite(payout) || payout < 0) {
    return { data: null, error: { message: 'Payout amount cannot be negative.' } };
  }
  if (payout > gross) {
    return { data: null, error: { message: 'Payout amount cannot exceed gross fund value.' } };
  }
  if (!note) return { data: null, error: { message: 'A manager note is required.' } };

  const user = await getUser();
  const postPayoutValue = Number((gross - payout).toFixed(2));

  const { data, error: insertError } = await supabase
    .from('fund_value_settlements')
    .insert({
      fund_id: fundId,
      valuation_date: date,
      gross_fund_value: gross,
      payout_pool: payout,
      post_payout_value: postPayoutValue,
      manager_note: note,
      entry_type: 'settlement',
      created_by: user?.id || null,
    })
    .select('*')
    .single();

  if (insertError) return { data: null, error: insertError };

  const { error: updateError } = await supabase
    .from('fund_state')
    .update({
      total_equity: postPayoutValue,
      last_updated: new Date().toISOString(),
    })
    .eq('id', fundId);

  if (updateError) return { data, error: updateError };

  await logFundAudit({
    fundId,
    navDate: date,
    entityType: 'fund_value_settlements',
    entityId: data.id,
    actionType: 'fund_value_settlement_create',
    fieldName: 'total_equity',
    oldValue: {
      total_equity: Number(fund.total_equity || 0),
    },
    newValue: {
      total_equity: postPayoutValue,
      gross_fund_value: gross,
      payout_pool: payout,
    },
    reason: note,
    metadata: { entryType: 'settlement' },
  });

  await postFundValueSettlementJournal({
    fundId,
    settlementId: data.id,
    grossFundValue: gross,
    postPayoutValue,
    payoutAmount: payout,
    valuationDate: date,
  });

  return { data, error: null };
};

export const createNavSettlement = createFundValueSettlement;

export const createInvestorState = async ({
  userId,
  fundId,
  units,
  investedAmount,
  protectionType = 'none',
  name = null,
}) => {
  const normalizedName = String(name || '').trim() || null;

  // Step 0: If a name was provided, save it to the profiles table.
  // This sets the investor's display name so it shows up on the
  // Investor Management page and distribution reports.
  if (normalizedName) {
    const { error: profileUpdateError } = await supabase
      .from('profiles')
      .update({ username: normalizedName })
      .eq('id', userId);

    if (profileUpdateError) {
      return { data: null, error: profileUpdateError };
    }
  }

  // Step 1: Create the investor_state row
  const { data: investor, error: investorError } = await supabase
    .from('investor_state')
    .insert({
      user_id:         userId,
      fund_id:         fundId,
      invested_amount: investedAmount,
      protection_type: protectionType,
      protocol_election: protectionType === 'full_protection' ? 'protection' : 'growth',
    })
    .select()
    .single();

  if (investorError) return { data: null, error: investorError };

  // Step 2: Record the initial contribution in the dollar capital ledger
  const fund = await getFundState();
  const { error: ledgerError } = await recordSubscriberCapitalEvent({
    fundId,
    investorId: investor.id,
    eventType: 'contribution',
    amount: investedAmount,
    description: 'Initial subscriber contribution',
    sourceTable: 'investor_state',
    sourceId: investor.id,
    referenceId: `initial-contribution:${investor.id}`,
  });

  if (ledgerError) return { data: investor, error: ledgerError };

  await postContributionJournal({
    fundId,
    investorId: investor.id,
    amount: investedAmount,
    sourceTable: 'investor_state',
    sourceId: investor.id,
    referenceId: `initial-contribution:${investor.id}`,
    description: 'Initial subscriber contribution',
    entryType: 'contribution',
  });

  // Step 3: Update fund_state totals
  if (fund) {
    await supabase
      .from('fund_state')
      .update({
        total_invested: Number(fund.total_invested) + investedAmount,
        total_equity:   Number(fund.total_equity || 0) + investedAmount,
        last_updated:   new Date().toISOString(),
      })
      .eq('id', fundId);
  }

  try {
    await syncFundSubscribersToCrm({
      subscribers: [{
        ...investor,
        id: investor.id,
        user_id: userId,
        invested_amount: investedAmount,
        join_date: investor.join_date,
        profiles: { username: normalizedName },
      }],
    });
  } catch (crmSyncError) {
    console.error('CRM sync failed after subscriber creation:', crmSyncError);
  }

  return { data: investor, error: null };
};


// ── REMOVE INVESTOR ───────────────────────────────────────
// Deletes an investor_state row and adjusts fund_state totals.
// This frees their allocation back to the available pool.
//
// WHAT IT DOES:
//   1. Fetches the investor's current units and invested_amount
//   2. Subtracts those from fund_state totals (units, equity, invested)
//   3. Deletes the investor_state row
//   4. Records a redemption in the unit ledger for audit trail
export const removeInvestor = async (investorId, fundId) => {
  // Step 1: Get investor's current state
  const { data: investor, error: fetchErr } = await supabase
    .from('investor_state')
    .select('invested_amount, user_id')
    .eq('id', investorId)
    .single();

  if (fetchErr || !investor) {
    return { error: fetchErr || { message: 'Investor not found' } };
  }

  const invAmount   = Number(investor.invested_amount);

  // Step 2: Update fund_state — subtract investor's contribution
  const fund = await getFundState();
  if (fund) {
    const newInvested   = Math.max(Number(fund.total_invested) - invAmount, 0);
    const newEquity     = Math.max(Number(fund.total_equity || 0) - invAmount, 0);

    await supabase
      .from('fund_state')
      .update({
        total_invested: newInvested,
        total_equity:   newEquity,
        last_updated:   new Date().toISOString(),
      })
      .eq('id', fundId);

    // Audit trail — record the redemption
    await recordSubscriberCapitalEvent({
      fundId,
      investorId,
      eventType: 'redemption',
      amount: -invAmount,
      description: 'Investor removed by manager',
      sourceTable: 'investor_state',
      sourceId: investorId,
      referenceId: `subscriber-removal:${investorId}:${Date.now()}`,
    });
  }

  // Historical ledgers should remain for audit, but they cannot keep a hard
  // reference to a subscriber row that is about to be removed.
  const { error: unitDetachError } = await supabase
    .from('subscriber_capital_ledger')
    .update({ investor_id: null })
    .eq('investor_id', investorId);
  if (unitDetachError) return { error: unitDetachError };

  const { error: distributionDetachError } = await supabase
    .from('distributions')
    .update({ investor_id: null })
    .eq('investor_id', investorId);
  if (distributionDetachError) return { error: distributionDetachError };

  // Step 3: Delete the investor_state row
  const { error: deleteErr } = await supabase
    .from('investor_state')
    .delete()
    .eq('id', investorId);

  return { error: deleteErr };
};

export const stageSubscriberRemoval = async ({
  investorId,
  fundId,
  userId,
  reason,
}) => {
  const trimmedReason = String(reason || '').trim();
  if (!trimmedReason) {
    return { error: { message: 'A removal reason is required.' } };
  }

  const manager = await getUser();
  const { data: investor, error: fetchErr } = await supabase
    .from('investor_state')
    .select('id, invested_amount, user_id, removed_at')
    .eq('id', investorId)
    .single();

  if (fetchErr || !investor) {
    return { error: fetchErr || { message: 'Subscriber not found.' } };
  }
  if (investor.removed_at) {
    return { error: { message: 'Subscriber has already been marked removed.' } };
  }

  const invAmount = Number(investor.invested_amount || 0);
  const fund = await getFundState();
  if (!fund) {
    return { error: { message: 'Fund state not found.' } };
  }

  const newInvested = Math.max(Number(fund.total_invested || 0) - invAmount, 0);
  const newEquity = Math.max(Number(fund.total_equity || 0) - invAmount, 0);

  const { error: fundError } = await supabase
    .from('fund_state')
    .update({
      total_invested: newInvested,
      total_equity: newEquity,
      last_updated: new Date().toISOString(),
    })
    .eq('id', fundId);

  if (fundError) return { error: fundError };

  const removalTimestamp = new Date().toISOString();
  const { error: ledgerError } = await recordSubscriberCapitalEvent({
    fundId,
    investorId,
    eventType: 'redemption',
    amount: -invAmount,
    description: `Subscriber removed by manager. Reason: ${trimmedReason}`,
    sourceTable: 'investor_state',
    sourceId: investorId,
    referenceId: `subscriber-staged-removal:${investorId}:${Date.now()}`,
  });

  if (ledgerError) return { error: ledgerError };

  const { error: updateInvestorError } = await supabase
    .from('investor_state')
    .update({
      invested_amount: 0,
      removed_at: removalTimestamp,
      removal_reason: trimmedReason,
      removed_by: manager?.id || null,
      pending_auth_delete: true,
      protection_type: 'none',
    })
    .eq('id', investorId);

  if (updateInvestorError) return { error: updateInvestorError };

  const { error: profileError } = await supabase
    .from('profiles')
    .update({ status: 'removed' })
    .eq('id', userId || investor.user_id);

  if (profileError) return { error: profileError };

  await logFundAudit({
    fundId,
    investorId,
    entityType: 'investor_state',
    entityId: investorId,
    fieldName: 'status',
    oldValue: { value: 'approved' },
    newValue: { value: 'removed', removedAt: removalTimestamp },
    reason: trimmedReason,
    actionType: 'subscriber_removed',
  });

  return { error: null };
};

export const finalizeSubscriberRemoval = async ({
  investorId,
  userId,
}) => {
  if (!investorId || !userId) {
    return { error: { message: 'Subscriber ID and user ID are required.' } };
  }

  const { data: investor, error: fetchErr } = await supabase
    .from('investor_state')
    .select('id, removed_at, pending_auth_delete')
    .eq('id', investorId)
    .single();

  if (fetchErr || !investor) {
    return { error: fetchErr || { message: 'Subscriber not found.' } };
  }
  if (!investor.removed_at) {
    return { error: { message: 'Mark the subscriber removed before final cleanup.' } };
  }

  const { error: unitDetachError } = await supabase
    .from('subscriber_capital_ledger')
    .update({ investor_id: null })
    .eq('investor_id', investorId);
  if (unitDetachError) return { error: unitDetachError };

  const { error: distributionDetachError } = await supabase
    .from('distributions')
    .update({ investor_id: null })
    .eq('investor_id', investorId);
  if (distributionDetachError) return { error: distributionDetachError };

  const { error: deleteErr } = await supabase
    .from('investor_state')
    .delete()
    .eq('id', investorId);

  return { error: deleteErr };
};


// ── UPDATE PROTECTION ─────────────────────────────────────
// Changes an investor's protection type and/or lock status.
// Only the manager can call this (RLS enforced).
//
// PARAMETERS:
//   investorId     — investor_state.id
//   protectionType — 'full_protection' or 'none' (optional, only if changing type)
//   locked         — true/false (optional, only if changing lock status)
export const updateProtection = async (investorId, { protectionType, locked } = {}) => {
  const updates = {};
  if (protectionType !== undefined) {
    updates.protection_type = protectionType;
    updates.protocol_election = protectionType === 'full_protection' ? 'protection' : 'growth';
  }
  if (locked !== undefined) updates.protection_locked = locked;

  if (Object.keys(updates).length === 0) {
    return { error: { message: 'No changes provided' } };
  }

  const { error } = await supabase
    .from('investor_state')
    .update(updates)
    .eq('id', investorId);

  return { error };
};


// ── UPDATE SUBSCRIPTION CAPACITY ──────────────────────────
// Sets the total capital the fund is raising from external investors.
// Available allocation = capacity - total_invested (from investor_state).
// When available hits $0, the fund is "Fully Subscribed."
export const updateSubscriptionCapacity = async (fundId, capacity) => {
  const { error } = await supabase
    .from('fund_state')
    .update({
      subscription_capacity: capacity,
      last_updated: new Date().toISOString(),
    })
    .eq('id', fundId);

  return { error };
};

export const updateFundProtectionCapacity = async (fundId, capacity) => {
  const normalized = Number(capacity);
  if (!Number.isFinite(normalized) || normalized < 0) {
    return { error: { message: 'Fund protection capacity must be zero or greater.' } };
  }

  const { error } = await supabase
    .from('fund_state')
    .update({
      fund_protection_capacity: normalized,
      last_updated: new Date().toISOString(),
    })
    .eq('id', fundId);

  return { error };
};

export const updateProtectedAllocationApproval = async ({
  investorId,
  fundId,
  approvedAmount,
  reason,
}) => {
  const normalizedReason = String(reason || '').trim();
  const clearing = approvedAmount === null || approvedAmount === undefined || approvedAmount === '';
  const amount = clearing ? null : Number(approvedAmount);

  if (!clearing && (!Number.isFinite(amount) || amount < 0)) {
    return { error: { message: 'Approved protected allocation must be zero or greater.' } };
  }
  if (!clearing && !normalizedReason) {
    return { error: { message: 'A reason is required for protected allocation approvals.' } };
  }

  const manager = await getUser();
  const { data: current, error: currentError } = await supabase
    .from('investor_state')
    .select('approved_protected_amount, protection_approval_reason, protection_approved_at')
    .eq('id', investorId)
    .single();

  if (currentError || !current) {
    return { error: currentError || { message: 'Subscriber not found.' } };
  }

  const payload = clearing
    ? {
        approved_protected_amount: null,
        protection_approval_reason: null,
        protection_approved_by: null,
        protection_approved_at: null,
      }
    : {
        approved_protected_amount: amount,
        protection_approval_reason: normalizedReason,
        protection_approved_by: manager?.id || null,
        protection_approved_at: new Date().toISOString(),
      };

  const { error } = await supabase
    .from('investor_state')
    .update(payload)
    .eq('id', investorId);

  if (error) return { error };

  await logFundAudit({
    fundId,
    investorId,
    entityType: 'investor_state',
    entityId: investorId,
    actionType: clearing ? 'protection_approval_cleared' : 'protection_approval_updated',
    fieldName: 'approved_protected_amount',
    oldValue: {
      value: current.approved_protected_amount,
      reason: current.protection_approval_reason,
      approvedAt: current.protection_approved_at,
    },
    newValue: {
      value: payload.approved_protected_amount,
      reason: payload.protection_approval_reason,
      approvedAt: payload.protection_approved_at,
    },
    reason: clearing ? 'Protected allocation approval cleared.' : normalizedReason,
  });

  return { error: null };
};

export const saveFundImportBatch = async ({
  fundId,
  fileName,
  uploadedAt = null,
  metadata = {},
  events = [],
  notes = '',
}) => {
  if (!fundId) return { data: null, error: { message: 'Fund ID is required.' } };
  if (!fileName) return { data: null, error: { message: 'File name is required.' } };

  const unsupportedCount = events.filter((event) => event.status === 'unsupported' || event.eventType === 'unsupported').length;
  const appliedCount = events.filter((event) => event.status === 'applied').length;

  const { data: batch, error: batchError } = await supabase
    .from('fund_import_batches')
    .insert({
      fund_id: fundId,
      source: 'fund_json',
      file_name: fileName,
      file_uploaded_at: uploadedAt,
      raw_metadata: metadata || {},
      event_count: events.length,
      applied_count: appliedCount,
      unsupported_count: unsupportedCount,
      notes: String(notes || '').trim() || null,
    })
    .select('*')
    .single();

  if (batchError || !batch) return { data: null, error: batchError || { message: 'Could not save import batch.' } };

  if (events.length) {
    const rows = events.map((event) => ({
      batch_id: batch.id,
      fund_id: fundId,
      event_type: event.eventType || 'unsupported',
      event_date: event.date || null,
      amount: Number.isFinite(Number(event.amount)) ? Number(event.amount) : null,
      raw_action: event.action || null,
      raw_symbol: event.symbol || null,
      raw_description: event.description || null,
      raw_payload: event.raw || {},
      normalized_payload: {
        rowIndex: event.rowIndex,
        subscriberAccountId: event.subscriberAccountId || '',
        approvedProtectedAmount: event.approvedProtectedAmount ?? null,
        message: event.message || '',
      },
      status: event.status === 'unsupported' ? 'unsupported' : 'previewed',
      error_message: event.status === 'unsupported' ? (event.message || 'Unsupported event') : null,
    }));

    const { error: eventsError } = await supabase
      .from('fund_import_events')
      .insert(rows);
    if (eventsError) return { data: batch, error: eventsError };
  }

  return { data: batch, error: null };
};

export const updateFundTotalInvested = async (fundId, totalInvested, reason = '') => {
  const normalizedTotal = Number(totalInvested);

  if (!Number.isFinite(normalizedTotal) || normalizedTotal < 0) {
    return { error: { message: 'Total invested must be a non-negative number.' } };
  }

  const fund = await getFundState();
  if (!fund || fund.id !== fundId) {
    return { error: { message: 'Fund not found.' } };
  }

  const previousTotal = Number(fund.total_invested || 0);
  const roundedTotal = Number(normalizedTotal.toFixed(2));

  const { data, error } = await supabase
    .from('fund_state')
    .update({
      total_invested: roundedTotal,
      last_updated: new Date().toISOString(),
    })
    .eq('id', fundId)
    .select()
    .single();

  if (error) return { error };

  const { error: auditError } = await logFundAudit({
    fundId,
    entityType: 'fund_state',
    entityId: fundId,
    actionType: 'update_total_invested',
    fieldName: 'total_invested',
    oldValue: { value: previousTotal },
    newValue: { value: roundedTotal },
    reason: reason || 'Manager corrected Fund Report total invested.',
    metadata: { source: 'fund_report' },
  });

  return { data, error: auditError || null };
};


// ── TOGGLE PAYOUT WINDOW ──────────────────────────────────
// Opens or closes the fund-level payout window.
// When OPEN: investors can select their distribution preference.
// When CLOSED: distribution options are locked for all investors.
//
// Only the manager controls this. It's a single toggle on fund_state.
export const togglePayoutWindow = async (fundId, isOpen) => {
  const { error } = await supabase
    .from('fund_state')
    .update({
      payout_window_open: isOpen,
      payout_window_opened_at: isOpen ? new Date().toISOString() : null,
      last_updated: new Date().toISOString(),
    })
    .eq('id', fundId);

  return { error };
};


// ── UPDATE DISTRIBUTION PREFERENCE ──────────────────────────
// Lets an investor set their preferred distribution type for the
// next payout: 'reinvest', 'payout', or 'exit'.
// Only works when the payout window is open.
//
export const updateDistPreference = async (investorId, preference, redemptionProfile = null) => {
  // Validate the preference value
  const valid = ['reinvest', 'payout', 'exit'];
  if (!valid.includes(preference)) {
    return { error: { message: `Invalid preference: ${preference}` } };
  }

  const fund = await getFundState();
  if (!fund) return { error: { message: 'Fund not found' } };

  if (!fund.payout_window_open) {
    return { error: { message: 'Payout window is closed. Distribution preference changes are only allowed during an open window.' } };
  }

  if (['payout', 'exit'].includes(preference) && redemptionProfile) {
    const { data: investorRecord, error: investorError } = await supabase
      .from('investor_state')
      .select('id, user_id, subscriber_account_id')
      .eq('id', investorId)
      .single();

    if (investorError || !investorRecord) {
      return { error: investorError || { message: 'Subscriber account not found.' } };
    }

    const { data: profileRecord, error: profileError } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', investorRecord.user_id)
      .single();

    if (profileError || !profileRecord) {
      return { error: profileError || { message: 'Subscriber profile not found.' } };
    }

    const profileUpdate = await updatePayoutProfile({
      userId: investorRecord.user_id,
      ...redemptionProfile,
    });
    if (profileUpdate.error) return { error: profileUpdate.error };

    const redemption = await recordRedemptionInstruction({
      investor: {
        id: investorRecord.id,
        subscriberAccountId: investorRecord.subscriber_account_id,
        name: resolveProfileDisplayName(profileRecord.username, 'Subscriber'),
      },
      distributionChoice: preference,
      ...redemptionProfile,
    });
    if (redemption.error) return { error: redemption.error };
  }

  const { error } = await supabase
    .from('investor_state')
    .update({ distribution_pref: preference })
    .eq('id', investorId);

  return { error };
};

export const updateSubscriberPortalStatus = async (userId, status) => {
  const valid = ['approved', 'pending', 'rejected', 'removed'];
  if (!valid.includes(status)) {
    return { error: { message: `Invalid portal status: ${status}` } };
  }

  const { error } = await supabase
    .from('profiles')
    .update({ status })
    .eq('id', userId);

  return { error };
};


// ── ACTIVATE / DEACTIVATE INVESTOR PROTECTION ────────────
// Called by the investor during an open payout window.
// Sets protection_type and records the protected_value
// (units × current NAV at the moment of activation).
//
// NOTE: Requires `protected_value` column in investor_state.
//       SQL: ALTER TABLE investor_state ADD COLUMN IF NOT EXISTS protected_value NUMERIC;
export const activateInvestorProtection = async (investorId, activate = true) => {
  // Get investor's current units
  const { data: inv, error: invErr } = await supabase
    .from('investor_state')
    .select('units, protection_locked')
    .eq('id', investorId)
    .single();

  if (invErr || !inv) return { error: invErr || { message: 'Investor not found' } };
  if (inv.protection_locked) return { error: { message: 'Protection is locked by the manager.' } };

  const fund = await getFundState();
  if (!fund) return { error: { message: 'Fund not found' } };

  // Check payout window is open
  if (!fund.payout_window_open) {
    return { error: { message: 'Payout window is closed or has expired. Protection changes are only allowed during the active 7-day window.' } };
  }

  const currentNav = Number(fund.current_nav);
  const protectedValue = activate ? Number(inv.units) * currentNav : 0;

  const { error } = await supabase
    .from('investor_state')
    .update({
      protection_type:  activate ? 'full_protection' : 'none',
      protocol_election: activate ? 'protection' : 'growth',
      protected_value:  protectedValue,
    })
    .eq('id', investorId);

  return { error, protectedValue };
};


// ── RECORD DISTRIBUTION ───────────────────────────────────
// Records a monthly distribution for ONE investor.
//
// DISTRIBUTION MATH:
//   1. Total profit = (currentNav - watermark) × totalUnits
//   2. Manager takes 50% off the top → managerCut
//   3. Investor pool = remaining 50%
//   4. Each investor gets allocation % × investor pool
//      - Allocation % uses protected + unprotected active capital
//      - Protected allocation defaults to 20% of fund protection capacity unless approved
//   5. Downside absorption applies only to protected allocation
//   6. Fund overflow or unallocated remainder remains at manager discretion
//
// ROLLING WATERMARK:
//   After ANY distribution, HWM resets to post-distribution NAV.
//   New growth after a payout is treated as new profit, not recovery.
//
// NON-DILUTION RULE:
//   Payouts cannot reduce fund below total invested principal.
//
// PARAMETERS:
//   investorId — investor_state.id
//   fundId     — fund_state.id
//   period     — 'Mar 2026', etc.
//   distType   — 'reinvest', 'payout', 'partial_divest', 'exit'
//
// NOTE: The function calculates grossProfit, fee, and net internally
//       based on the investor's allocation % and protection status.
//       The caller only needs to pass investorId, fundId, period, distType.
export const recordDistribution = async ({
  investorId,
  fundId,
  period,
  distType,
  manualAdjustment = 0,
  source = 'fund_value_settlement',
  sourceSettlementId = null,
  settlementPool = null,
}) => {
  const fund = await getFundState();
  if (!fund) return { error: { message: 'Fund not found' } };

  const { data: investor, error: invError } = await supabase
    .from('investor_state')
    .select('id, invested_amount, approved_protected_amount, protection_approval_reason, distribution_pref')
    .eq('id', investorId)
    .single();

  if (invError || !investor) {
    return { error: { message: 'Investor not found' } };
  }

  const { data: activeRows } = await supabase
    .from('investor_state')
    .select('id, invested_amount')
    .eq('fund_id', fundId)
    .is('removed_at', null);
  const totalActiveCapital = (activeRows || [])
    .reduce((sum, row) => sum + Math.max(Number(row.invested_amount || 0), 0), 0);
  if (totalActiveCapital <= 0) {
    return { error: { message: 'No active subscriber capital is available for payout allocation.' } };
  }

  let settlement = null;
  if (sourceSettlementId) {
    const response = await supabase
      .from('fund_value_settlements')
      .select('*')
      .eq('id', sourceSettlementId)
      .maybeSingle();
    settlement = response.data || null;
  }
  if (!settlement) {
    settlement = await getLatestUnprocessedValuePayout(fundId);
  }

  const grossSubscriberPool = Number(settlementPool ?? settlement?.payout_pool ?? 0);
  if (!Number.isFinite(grossSubscriberPool) || grossSubscriberPool <= 0) {
    return { error: { message: 'No unprocessed payout pool is available. Create a fund value settlement with a payout pool first.' } };
  }

  const activeCapital = Math.max(Number(investor.invested_amount || 0), 0);
  const allocationPct = activeCapital / totalActiveCapital;
  const taxRate = Number(fund.tax_withholding_rate ?? DEFAULT_TAX_RATE);
  const poolTaxWithholding = grossSubscriberPool * taxRate;
  const poolPostTaxAmount = grossSubscriberPool - poolTaxWithholding;
  const taxWithholding = poolTaxWithholding * allocationPct;
  const postTaxShare = poolPostTaxAmount * allocationPct;
  const grossProfit = grossSubscriberPool * allocationPct;
  const managerAdjustment = Math.max(Number(manualAdjustment) || 0, 0);
  const adjustedNet = postTaxShare + managerAdjustment;
  const adjustedGrossPayoutImpact = grossProfit + managerAdjustment;
  const protectionAllocation = deriveProtectionAllocation({ activeCapital, fund, investor });

  let effectiveType = distType;
  const capacity = Number(fund.subscription_capacity) || 0;
  if (distType === 'reinvest' && capacity > 0) {
    const available = capacity - totalActiveCapital;
    if (adjustedNet > available) effectiveType = 'payout';
  }
  const effectiveIsPayout = effectiveType !== 'reinvest';

  const { data: distributionRecord, error: distError } = await supabase
    .from('distributions')
    .insert({
      fund_id: fundId,
      investor_id: investorId,
      period,
      dist_type: effectiveType,
      gross_profit: grossProfit,
      tax_rate: taxRate,
      tax_withholding: taxWithholding,
      post_tax_share: postTaxShare,
      allocation_pct: allocationPct,
      gross_subscriber_pool: grossSubscriberPool,
      pool_tax_withholding: poolTaxWithholding,
      pool_post_tax_amount: poolPostTaxAmount,
      protected_allocation: protectionAllocation.protectedAllocation,
      unprotected_allocation: protectionAllocation.unprotectedAllocation,
      protection_capacity: protectionAllocation.fundProtectionCapacity,
      protection_status: protectionAllocation.protectionStatus,
      manual_adjustment: managerAdjustment,
      fee: 0,
      net: adjustedNet,
    })
    .select('id')
    .single();

  if (distError) return { error: distError };

  await postDistributionJournal({
    fundId,
    investorId,
    amount: adjustedNet,
    taxWithholding,
    fee: 0,
    sourceId: distributionRecord?.id,
    referenceId: `distribution:${distributionRecord?.id || `${investorId}:${period}`}`,
    period,
    reinvest: effectiveType === 'reinvest',
  });

  if (effectiveType === 'reinvest' && adjustedNet > 0) {
    await supabase
      .from('investor_state')
      .update({ invested_amount: activeCapital + adjustedNet })
      .eq('id', investorId);

    await supabase
      .from('fund_state')
      .update({
        total_invested: Number(fund.total_invested || 0) + adjustedNet,
        total_equity: Number(fund.total_equity || 0) + adjustedNet,
        last_updated: new Date().toISOString(),
      })
      .eq('id', fundId);

    await recordSubscriberCapitalEvent({
      fundId,
      investorId,
      eventType: 'reinvestment',
      amount: adjustedNet,
      description: `${period} reinvested distribution`,
      sourceTable: 'distributions',
      sourceId: distributionRecord?.id,
      referenceId: `distribution-reinvestment:${distributionRecord?.id}`,
      metadata: {
        allocation_pct: allocationPct,
        protected_allocation: protectionAllocation.protectedAllocation,
        unprotected_allocation: protectionAllocation.unprotectedAllocation,
      },
    });
  }

  if (effectiveIsPayout) {
    await supabase
      .from('fund_state')
      .update({
        total_equity: Math.max(Number(fund.total_equity || 0) - adjustedGrossPayoutImpact, 0),
        last_updated: new Date().toISOString(),
      })
      .eq('id', fundId);
  }

  return {
    error: null,
    breakdown: {
      totalProfit: grossSubscriberPool,
      managerCut: 0,
      investorPool: poolPostTaxAmount,
      investorShare: postTaxShare,
      grossSubscriberPool,
      poolTaxWithholding,
      poolPostTaxAmount,
      allocation: allocationPct * 100,
      postTaxShare,
      taxWithholding,
      protectionAdjustment: 0,
      protectedAllocation: protectionAllocation.protectedAllocation,
      unprotectedAllocation: protectionAllocation.unprotectedAllocation,
      protectionCapacity: protectionAllocation.fundProtectionCapacity,
      protectionStatus: protectionAllocation.protectionStatus,
      grossProfit,
      fee: 0,
      finalNet: adjustedNet,
      net: adjustedNet,
      managerAdjustment,
      source,
      sourceSettlementId: settlement?.id || sourceSettlementId || null,
      unitsIssued: 0,
      isProtected: protectionAllocation.protectedAllocation > 0,
      effectiveType,
      wasAutoConverted: effectiveType !== distType,
    },
  };
};

// eslint-disable-next-line no-unused-vars
const legacyRecordDistribution = async ({
  investorId,
  fundId,
  period,
  distType,
  manualAdjustment = 0,
  source = 'hwm_profit',
  sourceSettlementId = null,
  settlementPool = null,
  batchHwm = null,   // BUG 2 FIX: When processing a batch, pass the original HWM
                      // so each investor is measured against the same watermark.
                      // When null, falls back to fresh fund_state HWM (solo mode).
}) => {
  // ── Fetch fund state ───────────────────────────────────
  const fund = await getFundState();
  if (!fund) return { error: { message: 'Fund not found' } };

  const currentNav    = Number(fund.current_nav);
  const totalEquity   = Number(fund.total_equity);
  const totalUnits    = Number(fund.total_units);
  const totalInvested = Number(fund.total_invested);
  const capacity      = Number(fund.subscription_capacity) || 0;
  // Use batchHwm if provided (batch mode), otherwise use current fund HWM (solo mode)
  const effectiveHwm  = batchHwm !== null ? Number(batchHwm) : Number(fund.high_water_mark);
  const isBatchMode   = batchHwm !== null;
  const isSettlementSource = source === 'settlement' && Boolean(sourceSettlementId);

  // ── Fetch this investor's state ────────────────────────
  const { data: investor, error: invError } = await supabase
    .from('investor_state')
    .select('units, invested_amount, approved_protected_amount, protection_approval_reason')
    .eq('id', investorId)
    .single();

  if (invError || !investor) {
    return { error: { message: 'Investor not found' } };
  }

  const investedAmount  = Number(investor.invested_amount);
  const protectionAllocation = deriveProtectionAllocation({
    activeCapital: investedAmount,
    fund,
    investor,
  });
  const totalSubscribed = Number(totalInvested || 0);
  const allocationCapacity = capacity > 0 ? capacity : (isSettlementSource ? totalSubscribed : 0);

  // ── GUARD 1: Recovery mode ─────────────────────────────
  // NAV must exceed the watermark for any distribution.
  // In batch mode, compare against the ORIGINAL HWM captured before the loop,
  // not the mid-batch HWM that gets reset after each payout.
  if (!isSettlementSource && currentNav <= effectiveHwm) {
    return { error: { message: 'Recovery mode — NAV has not exceeded the watermark. No distributions allowed.' } };
  }

  // ── GUARD 2: Subscription capacity must be set ─────────
  if (allocationCapacity <= 0) {
    return { error: { message: 'Subscription capacity not set. Manager must set a target raise before distributions.' } };
  }

  // ── CALCULATE DISTRIBUTION ─────────────────────────────
  // Step 1: Total distributable profit across the entire fund
  // Uses effectiveHwm so all investors in a batch share the same profit baseline.
  const taxRate = Number(fund.tax_withholding_rate ?? DEFAULT_TAX_RATE);
  const calculation = calculateDistributionBreakdown({
    currentNav,
    effectiveHwm,
    totalUnits,
    totalEquity,
    totalInvested,
    capacity: allocationCapacity,
    investedAmount,
    protectedAllocation: protectionAllocation.protectedAllocation,
    unprotectedAllocation: protectionAllocation.unprotectedAllocation,
    protectionStatus: protectionAllocation.protectionStatus,
    taxRate,
    grossSubscriberPoolOverride: isSettlementSource ? Number(settlementPool || 0) : null,
  });

  const {
    totalProfit,
    managerCut,
    grossSubscriberPool,
    poolTaxWithholding,
    poolPostTaxAmount,
    allocationPct,
    postTaxShare,
    protectionAdjustment,
    grossProfit,
    fee,
    net,
    grossPayoutImpact,
  } = calculation;
  const managerAdjustment = Math.max(Number(manualAdjustment) || 0, 0);
  const adjustedNet = net + managerAdjustment;
  const adjustedGrossPayoutImpact = grossPayoutImpact + managerAdjustment;

  // ── GUARD 3: Block reinvest when no allocation space ────
  // If the investor chose reinvest but it would push them past the
  // subscription capacity, auto-convert to payout.
  let effectiveType = distType;
  if (distType === 'reinvest' && capacity > 0) {
    const allInvestors = await supabase
      .from('investor_state')
      .select('invested_amount');
    const totalSubscribed = (allInvestors.data || []).reduce((s, i) => s + Number(i.invested_amount), 0);
    const available = capacity - totalSubscribed;
    if (adjustedNet > available) {
      effectiveType = 'payout'; // Force payout — no room to reinvest
    }
  }
  const effectiveIsPayout = effectiveType !== 'reinvest';

  // ── GUARD 4: Non-dilution check ────────────────────────
  // Only for payouts (including auto-converted reinvests) — ensure fund stays above total principal
  if (!isSettlementSource && effectiveIsPayout) {
    const postPayoutEquity = totalEquity - adjustedGrossPayoutImpact;
    if (postPayoutEquity < totalInvested) {
      return { error: { message: `Non-dilution violation — payout of $${adjustedGrossPayoutImpact.toFixed(2)} would reduce fund below principal ($${totalInvested.toFixed(2)}).` } };
    }
  }

  // ── Calculate units issued (reinvest only) ─────────────
  // Net profit converts to new units at current NAV
  const unitsIssued = effectiveType === 'reinvest' ? adjustedNet / currentNav : 0;

  // ── Step 1: Insert the distribution record ─────────────
  const { data: distributionRecord, error: distError } = await supabase
    .from('distributions')
    .insert({
      fund_id:      fundId,
      investor_id:  investorId,
      period,
      dist_type:    effectiveType,
      gross_profit: grossProfit,
      tax_rate: taxRate,
      tax_withholding: poolTaxWithholding * allocationPct,
      post_tax_share: postTaxShare,
      allocation_pct: allocationPct,
      gross_subscriber_pool: grossSubscriberPool,
      pool_tax_withholding: poolTaxWithholding,
      pool_post_tax_amount: poolPostTaxAmount,
      protected_allocation: protectionAllocation.protectedAllocation,
      unprotected_allocation: protectionAllocation.unprotectedAllocation,
      protection_capacity: protectionAllocation.fundProtectionCapacity,
      protection_status: protectionAllocation.protectionStatus,
      manual_adjustment: managerAdjustment,
      source_settlement_id: sourceSettlementId || null,
      fee,
      net: adjustedNet,
      units_issued: unitsIssued,
    })
    .select('id')
    .single();

  if (distError) return { error: distError };

  await postDistributionJournal({
    fundId,
    investorId,
    amount: adjustedNet,
    taxWithholding: poolTaxWithholding * allocationPct,
    fee,
    sourceId: distributionRecord?.id,
    referenceId: `distribution:${distributionRecord?.id || `${investorId}:${period}`}`,
    period,
    reinvest: effectiveType === 'reinvest',
  });

  // ── Step 2: Handle reinvest — create new units ─────────
  if (effectiveType === 'reinvest' && unitsIssued > 0) {
    // Add to the unit ledger (audit trail)
    await supabase
      .from('fund_units')
      .insert({
        fund_id:      fundId,
        investor_id:  investorId,
        tx_type:      'distribution',
        units:        unitsIssued,
        unit_price:   currentNav,
        total_amount: adjustedNet,
        nav_at_tx:    currentNav,
        hwm_at_tx:    effectiveHwm,
        notes:        `${period} reinvested - alloc ${(allocationPct * 100).toFixed(1)}%, protected $${protectionAllocation.protectedAllocation.toFixed(2)}, unprotected $${protectionAllocation.unprotectedAllocation.toFixed(2)}${managerAdjustment > 0 ? `, manager adjustment $${managerAdjustment.toFixed(2)}` : ''}`,
      });

    // Update investor's unit balance
    const currentUnits = Number(investor.units);
    await supabase
      .from('investor_state')
      .update({ units: currentUnits + unitsIssued })
      .eq('id', investorId);

    // Reinvest: equity stays the same, units increase, NAV unchanged
    // In batch mode, skip HWM reset — the batch processor resets once at the end.
    const newTotalUnits = totalUnits + unitsIssued;
    const reinvestUpdate = {
      total_units:     newTotalUnits,
      external_units:  Number(fund.external_units) + unitsIssued,
      total_equity:    newTotalUnits * currentNav,
      last_updated:    new Date().toISOString(),
    };
    // Only reset HWM when NOT in batch mode (solo distribution)
    if (!isBatchMode) {
      reinvestUpdate.high_water_mark = currentNav;     // ROLLING WATERMARK RESET
      reinvestUpdate.recovery_mode   = false;          // fresh cycle starts
    }
    await supabase
      .from('fund_state')
      .update(reinvestUpdate)
      .eq('id', fundId);
  }

  // ── Step 3: Handle payout — money leaves the fund ──────
  if (effectiveIsPayout && !isSettlementSource) {
    // The investor's post-tax share leaves the fund. The legacy fee column
    // remains zero under the protected/unprotected allocation model.
    // This reduces total equity and therefore NAV per unit
    const newEquity = totalEquity - adjustedGrossPayoutImpact;
    const newNav    = totalUnits > 0 ? newEquity / totalUnits : 0;

    const payoutUpdate = {
      current_nav:     newNav,
      total_equity:    newEquity,
      last_updated:    new Date().toISOString(),
    };
    // Only reset HWM when NOT in batch mode (solo distribution)
    if (!isBatchMode) {
      payoutUpdate.high_water_mark = newNav;           // ROLLING WATERMARK RESET
      payoutUpdate.recovery_mode   = false;            // fresh cycle starts
    }
    await supabase
      .from('fund_state')
      .update(payoutUpdate)
      .eq('id', fundId);

    // Snapshot post-payout NAV in history
    await supabase
      .from('nav_history')
      .upsert(
        { fund_id: fundId, date: new Date().toISOString().split('T')[0], nav: newNav },
        { onConflict: 'fund_id,date' }
      );
  }

  // Return the calculated breakdown so the UI can display it
  return {
    error: null,
    breakdown: {
      totalProfit,
      managerCut,
      investorPool: poolPostTaxAmount,
      investorShare: postTaxShare,
      grossSubscriberPool,
      poolTaxWithholding,
      poolPostTaxAmount,
      allocation: allocationPct * 100,
      postTaxShare,
      taxWithholding: poolTaxWithholding * allocationPct,
      protectionAdjustment,
      protectedAllocation: protectionAllocation.protectedAllocation,
      unprotectedAllocation: protectionAllocation.unprotectedAllocation,
      protectionCapacity: protectionAllocation.fundProtectionCapacity,
      protectionStatus: protectionAllocation.protectionStatus,
      grossProfit,
      fee,
      finalNet: adjustedNet,
      net: adjustedNet,
      managerAdjustment,
      source,
      sourceSettlementId,
      unitsIssued,
      isProtected: protectionAllocation.protectedAllocation > 0,
      effectiveType,       // may differ from distType if reinvest was auto-converted
      wasAutoConverted: effectiveType !== distType,
    },
  };
};


// ── RESET HWM AFTER BATCH ─────────────────────────────────
// Called ONCE after all investors in a batch have been processed.
// Reads the current fund state (which reflects all payouts/reinvests)
// and sets HWM to the post-batch NAV. This is the single watermark
// reset that was deferred during batch processing.
export const resetHwmAfterBatch = async (fundId) => {
  return { error: null, fundId };
};


// ── CAN DISTRIBUTE (Authorization + Preview) ─────────────
// Checks if the fund is eligible for distributions and returns
// a full breakdown showing each investor's projected payout.
//
// DISTRIBUTION MATH PREVIEW:
//   1. Total profit = (NAV - watermark) × total units
//   2. Manager takes 50% off the top
//   3. Investor pool = remaining 50%
//   4. Each investor gets allocation % × pool using active capital
//   5. Protected/unprotected allocations are shown separately for downside treatment
//
// USAGE:
//   Call this before showing the distribution form to the manager.
//   Returns the full breakdown so the manager can review before confirming.
export const canDistribute = async () => {
  const fund = await getFundState();
  if (!fund) return { authorized: false, reason: 'Fund not found', profit: 0 };

  const settlement = await getLatestUnprocessedValuePayout(fund.id);
  if (!settlement) {
    return {
      authorized: false,
      reason: 'No unprocessed payout pool is available. Create a fund value settlement with a payout pool first.',
      profit: 0,
      investorPool: 0,
      breakdown: [],
    };
  }

  const grossSubscriberPool = Number(settlement.payout_pool || 0);
  if (grossSubscriberPool <= 0) {
    return {
      authorized: false,
      reason: 'Latest fund value settlement has no payout pool.',
      profit: 0,
      investorPool: 0,
      breakdown: [],
    };
  }

  const { data: investors } = await supabase
    .from('investor_state')
    .select('id, user_id, invested_amount, approved_protected_amount, protection_approval_reason, distribution_pref')
    .eq('fund_id', fund.id)
    .is('removed_at', null);

  const totalActiveCapital = (investors || [])
    .reduce((sum, inv) => sum + Math.max(Number(inv.invested_amount || 0), 0), 0);
  if (totalActiveCapital <= 0) {
    return {
      authorized: false,
      reason: 'No active subscriber capital found for allocating the payout pool.',
      profit: 0,
      investorPool: 0,
      breakdown: [],
    };
  }

  const invUserIds = (investors || []).map((inv) => inv.user_id).filter(Boolean);
  const { data: invProfiles } = invUserIds.length
    ? await supabase.from('profiles').select('id, username').in('id', invUserIds)
    : { data: [] };
  const profileLookup = {};
  (invProfiles || []).forEach((profile) => { profileLookup[profile.id] = profile.username; });

  const taxRate = Number(fund.tax_withholding_rate ?? DEFAULT_TAX_RATE);
  const poolTaxWithholding = grossSubscriberPool * taxRate;
  const poolPostTaxAmount = grossSubscriberPool - poolTaxWithholding;
  const breakdown = (investors || []).map((inv) => {
    const invested = Math.max(Number(inv.invested_amount || 0), 0);
    const allocationPct = invested / totalActiveCapital;
    const protectionAllocation = deriveProtectionAllocation({ activeCapital: invested, fund, investor: inv });
    return {
      investorId: inv.id,
      name: resolveProfileDisplayName(profileLookup[inv.user_id], 'Subscriber'),
      invested,
      allocation: allocationPct * 100,
      share: poolPostTaxAmount * allocationPct,
      grossShare: grossSubscriberPool * allocationPct,
      taxWithholding: poolTaxWithholding * allocationPct,
      postTaxShare: poolPostTaxAmount * allocationPct,
      protectionAdjustment: 0,
      protectedAllocation: protectionAllocation.protectedAllocation,
      unprotectedAllocation: protectionAllocation.unprotectedAllocation,
      protectionStatus: protectionAllocation.protectionStatus,
      distributionPref: inv.distribution_pref || 'payout',
      sourceSettlementId: settlement.id,
    };
  });

  return {
    authorized: true,
    reason: 'Fund value settlement payout pool ready.',
    profit: grossSubscriberPool,
    totalProfit: grossSubscriberPool,
    managerCut: 0,
    investorPool: poolPostTaxAmount,
    poolTaxWithholding,
    poolPostTaxAmount,
    taxRate,
    settlement,
    source: 'fund_value_settlement',
    breakdown,
  };
};

// eslint-disable-next-line no-unused-vars
const legacyCanDistribute = async () => {
  const fund = await getFundState();
  if (!fund) return { authorized: false, reason: 'Fund not found', profit: 0 };

  const currentNav = Number(fund.current_nav);
  const hwm        = Number(fund.high_water_mark);
  const totalUnits = Number(fund.total_units);
  const capacity   = Number(fund.subscription_capacity) || 0;

  // Check if NAV exceeds the watermark
  if (currentNav <= hwm && totalUnits < 0) {
    return {
      authorized: false,
      reason:     'Recovery mode — NAV has not exceeded the watermark. No distributions until fund grows past the current watermark.',
      profit:     0,
    };
  }

  // Check subscription capacity is set
  const settlement = await getLatestUnprocessedSettlementPayout(fund.id);
  if (settlement) {
    const taxRate = Number(fund.tax_withholding_rate ?? DEFAULT_TAX_RATE);
    const grossSubscriberPool = Number(settlement.payout_amount || 0);
    const poolTaxWithholding = grossSubscriberPool * taxRate;
    const poolPostTaxAmount = grossSubscriberPool - poolTaxWithholding;

    const { data: investors } = await supabase
      .from('investor_state')
      .select('id, user_id, units, invested_amount, approved_protected_amount, protection_approval_reason, distribution_pref');

    const invUserIds = (investors || []).map(inv => inv.user_id);
    const { data: invProfiles } = await supabase
      .from('profiles')
      .select('id, username')
      .in('id', invUserIds);

    const profileLookup = {};
    (invProfiles || []).forEach(p => { profileLookup[p.id] = p.username; });

    const totalSubscribed = (investors || []).reduce((sum, inv) => sum + Number(inv.invested_amount || 0), 0);
    const allocationCapacity = capacity > 0 ? capacity : totalSubscribed;

    if (allocationCapacity <= 0) {
      return {
        authorized: false,
        reason: 'No subscribed capital found for allocating the settlement payout.',
        profit: 0,
      };
    }

    const breakdown = (investors || []).map(inv => {
      const invested = Number(inv.invested_amount);
      const protectionAllocation = deriveProtectionAllocation({
        activeCapital: invested,
        fund,
        investor: inv,
      });
      const investorBreakdown = calculateDistributionBreakdown({
        currentNav,
        effectiveHwm: hwm,
        totalUnits,
        totalEquity: Number(fund.total_equity || 0),
        totalInvested: Number(fund.total_invested || 0),
        capacity: allocationCapacity,
        investedAmount: invested,
        protectedAllocation: protectionAllocation.protectedAllocation,
        unprotectedAllocation: protectionAllocation.unprotectedAllocation,
        protectionStatus: protectionAllocation.protectionStatus,
        taxRate,
        grossSubscriberPoolOverride: grossSubscriberPool,
      });

      return {
        investorId: inv.id,
        name: resolveProfileDisplayName(profileLookup[inv.user_id], 'Subscriber'),
        invested,
        allocation: investorBreakdown.allocationPct * 100,
        share: investorBreakdown.postTaxShare,
        grossSubscriberPool,
        poolTaxWithholding,
        poolPostTaxAmount,
        postTaxShare: investorBreakdown.postTaxShare,
        taxWithholding: poolTaxWithholding * investorBreakdown.allocationPct,
        premium: investorBreakdown.protectionAdjustment,
        net: investorBreakdown.finalNet,
        protectedAllocation: protectionAllocation.protectedAllocation,
        unprotectedAllocation: protectionAllocation.unprotectedAllocation,
        protectionCapacity: protectionAllocation.fundProtectionCapacity,
        protectionStatus: protectionAllocation.protectionStatus,
        isProtected: protectionAllocation.protectedAllocation > 0,
        preference: inv.distribution_pref || 'payout',
      };
    });

    return {
      authorized: true,
      reason: null,
      source: 'settlement',
      settlementId: settlement.id,
      settlement,
      profit: grossSubscriberPool,
      managerCut: 0,
      taxRate,
      investorPool: poolPostTaxAmount,
      grossSubscriberPool,
      poolTaxWithholding,
      poolPostTaxAmount,
      perUnit: totalUnits > 0 ? grossSubscriberPool / totalUnits : 0,
      breakdown,
    };
  }

  if (capacity <= 0) {
    return {
      authorized: false,
      reason:     'Subscription capacity not set. Set a target raise before processing HWM-profit distributions.',
      profit:     0,
    };
  }

  if (currentNav <= hwm) {
    return {
      authorized: false,
      reason:     'Recovery mode - NAV has not exceeded the watermark and no unprocessed settlement payout is available.',
      profit:     0,
    };
  }

  const totalProfit  = (currentNav - hwm) * totalUnits;
  const managerCut   = totalProfit * 0.5;
  const taxRate      = Number(fund.tax_withholding_rate ?? DEFAULT_TAX_RATE);
  const grossSubscriberPool = totalProfit - managerCut;
  const poolTaxWithholding = grossSubscriberPool * taxRate;
  const poolPostTaxAmount = grossSubscriberPool - poolTaxWithholding;

  // Fetch all investors (no profiles join — same reason as getAllInvestors)
  const { data: investors } = await supabase
    .from('investor_state')
    .select('id, user_id, units, invested_amount, approved_protected_amount, protection_approval_reason, distribution_pref');

  // Fetch profile names separately
  const invUserIds = (investors || []).map(inv => inv.user_id);
  const { data: invProfiles } = await supabase
    .from('profiles')
    .select('id, username')
    .in('id', invUserIds);

  const profileLookup = {};
  (invProfiles || []).forEach(p => { profileLookup[p.id] = p.username; });

  const breakdown = (investors || []).map(inv => {
    const invested = Number(inv.invested_amount);
    const protectionAllocation = deriveProtectionAllocation({
      activeCapital: invested,
      fund,
      investor: inv,
    });
    const investorBreakdown = calculateDistributionBreakdown({
      currentNav,
      effectiveHwm: hwm,
      totalUnits,
      totalEquity: Number(fund.total_equity || 0),
      totalInvested: Number(fund.total_invested || 0),
      capacity,
      investedAmount: invested,
      protectedAllocation: protectionAllocation.protectedAllocation,
      unprotectedAllocation: protectionAllocation.unprotectedAllocation,
      protectionStatus: protectionAllocation.protectionStatus,
      taxRate,
    });

    return {
      investorId:   inv.id,
      name:         resolveProfileDisplayName(profileLookup[inv.user_id], 'Subscriber'),
      invested,
      allocation: investorBreakdown.allocationPct * 100,
      share: investorBreakdown.postTaxShare,
      grossSubscriberPool,
      poolTaxWithholding,
      poolPostTaxAmount,
      postTaxShare: investorBreakdown.postTaxShare,
      taxWithholding: poolTaxWithholding * investorBreakdown.allocationPct,
      premium: investorBreakdown.protectionAdjustment,
      net: investorBreakdown.finalNet,
      protectedAllocation: protectionAllocation.protectedAllocation,
      unprotectedAllocation: protectionAllocation.unprotectedAllocation,
      protectionCapacity: protectionAllocation.fundProtectionCapacity,
      protectionStatus: protectionAllocation.protectionStatus,
      isProtected: protectionAllocation.protectedAllocation > 0,
      preference:   inv.distribution_pref || 'payout',
    };
  });

  return {
    authorized:  true,
    reason:      null,
    source:      'hwm_profit',
    settlementId: null,
    profit:      totalProfit,
    managerCut,
    taxRate,
    investorPool: poolPostTaxAmount,
    grossSubscriberPool,
    poolTaxWithholding,
    poolPostTaxAmount,
    perUnit:     currentNav - hwm,
    breakdown,
  };
};


// ── SAVE APPLICATION DETAILS ──────────────────────────────
// Called after a new investor signs up from the InvestorLogin page.
// Updates the profiles row (auto-created by DB trigger) with:
//   - status = 'pending' (awaiting manager approval)
//   - phone, requested_amount, requested_protection, notes, applied_at
//   - optional legal acknowledgment summary appended into notes
//
// The manager sees these in the "Pending Applications" section on
// the Investors page and can approve or reject.
export const saveApplicationDetails = async (userId, {
  username = null,
  phone = null,
  requestedAmount = 0,
  requestedProtection = 'none',
  zelleName = null,
  zelleEmail = null,
  zellePhone = null,
  notes = null,
  legalConsents = null,
}) => {
  const normalizedUsername = String(username || '').trim() || null;
  const payoutProfile = normalizeZelleProfile({ zelleName, zelleEmail, zellePhone });
  const normalizedNotes = String(notes || '').trim();
  const reviewedAt = legalConsents?.reviewedAt || new Date().toISOString();
  const acceptedCount = legalConsents?.documents?.length || legalConsents?.acceptedDocuments?.length || 0;
  const combinedNotes = [
    normalizedNotes,
    acceptedCount
      ? `Electronic acknowledgment recorded for ${acceptedCount} onboarding document${acceptedCount === 1 ? '' : 's'} on ${new Date(reviewedAt).toLocaleString()}.`
      : '',
  ].filter(Boolean).join('\n\n') || null;

  const profilePayload = {
    ...(normalizedUsername ? { username: normalizedUsername } : {}),
    status:               'pending',
    phone:                phone,
    requested_amount:     requestedAmount,
    requested_protection: requestedProtection,
    zelle_name:           payoutProfile.zelleName || null,
    zelle_email:          payoutProfile.zelleEmail || null,
    zelle_phone:          payoutProfile.zellePhone || null,
    zelle_updated_at:     payoutProfile.zelleName || payoutProfile.zelleEmail || payoutProfile.zellePhone ? new Date().toISOString() : null,
    notes:                combinedNotes,
    applied_at:           new Date().toISOString(),
  };

  const { data: updatedProfile, error } = await supabase
    .from('profiles')
    .update(profilePayload)
    .eq('id', userId)
    .select('id')
    .maybeSingle();

  if (error) return { error };

  if (!updatedProfile) {
    const { error: upsertError } = await supabase
      .from('profiles')
      .upsert({ id: userId, role: 'investor', ...profilePayload }, { onConflict: 'id' });

    if (upsertError) return { error: upsertError };
  }

  const { error: documentError, documents } = await saveApplicationDocuments(userId, legalConsents);

  if (!documentError) {
    await logSubscriberDelivery({
      userId,
      title: 'New application received',
      deliveryStatus: 'recorded',
      details: {
        notification_type: 'manager_new_application',
        requested_amount: requestedAmount,
        requested_protection: requestedProtection,
        zelle_name_on_file: Boolean(payoutProfile.zelleName),
        zelle_contact_on_file: Boolean(payoutProfile.zelleEmail || payoutProfile.zellePhone),
        documents_recorded: documents.length,
      },
    });
  }

  return { error: documentError };
};


// ── GET PENDING APPLICATIONS ──────────────────────────────
// Returns all profiles with status='pending' — these are investors
// who applied but haven't been approved yet.
// Only the manager should call this (RLS enforced).
export const getPendingApplications = async () => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, role, status, phone, requested_amount, requested_protection, zelle_name, zelle_email, zelle_phone, zelle_updated_at, notes, applied_at')
    .eq('status', 'pending')
    .eq('role', 'investor')
    .order('applied_at', { ascending: true });

  if (error) return { data: [], error };
  return { data: data || [], error: null };
};


// ── APPROVE APPLICATION ───────────────────────────────────
// Manager approves a pending investor:
//   1. Updates profiles.status → 'approved'
//   2. Creates investor_state row (units at current NAV)
//   3. Updates fund_state totals
//
// Uses the existing createInvestorState() function for step 2+3.
export const approveApplication = async ({
  pendingProfileId,
  resolvedAuthUserId,
  resolutionMode = 'auto',
  fundId,
}) => {
  const targetUserId = resolvedAuthUserId || pendingProfileId;
  if (!pendingProfileId || !targetUserId) {
    return { error: { message: 'A pending profile id and resolved auth user id are required.' } };
  }

  const { data: pendingProfile, error: profileError } = await supabase
    .from('profiles')
    .select('id, username, role, status, phone, requested_amount, requested_protection, zelle_name, zelle_email, zelle_phone, zelle_updated_at, notes, applied_at')
    .eq('id', pendingProfileId)
    .limit(1)
    .maybeSingle();

  if (profileError || !pendingProfile) {
    return { error: profileError || { message: 'Pending application profile not found.' } };
  }

  const { data: existingInvestor, error: investorLookupError } = await supabase
    .from('investor_state')
    .select('id')
    .eq('user_id', targetUserId)
    .eq('fund_id', fundId)
    .is('removed_at', null)
    .limit(1)
    .maybeSingle();

  if (investorLookupError) return { error: investorLookupError };
  if (existingInvestor?.id) {
    return { error: { message: 'This Supabase user already has an active subscriber account for the fund.' } };
  }

  const { data: targetProfile } = await supabase
    .from('profiles')
    .select('id, username, role, status, phone, requested_amount, requested_protection, zelle_name, zelle_email, zelle_phone, zelle_updated_at, notes, applied_at')
    .eq('id', targetUserId)
    .limit(1)
    .maybeSingle();

  const { data: applicationDocuments, error: docsError } = await supabase
    .from('subscriber_application_documents')
    .select('document_slug, title, version, accepted_at, acceptance_method, snapshot_path, snapshot_payload')
    .eq('user_id', pendingProfileId);

  if (docsError) return { error: docsError };
  if (!applicationDocuments?.length) {
    return { error: { message: 'Application documents are missing. Record document acknowledgments before approval.' } };
  }

  const mergedProfile = {
    id: targetUserId,
    username: pendingProfile.username || targetProfile?.username || 'Subscriber',
    role: 'investor',
    status: 'approved',
    phone: pendingProfile.phone || targetProfile?.phone || null,
    requested_amount: pendingProfile.requested_amount ?? targetProfile?.requested_amount ?? null,
    requested_protection: pendingProfile.requested_protection || targetProfile?.requested_protection || 'none',
    zelle_name: pendingProfile.zelle_name || targetProfile?.zelle_name || null,
    zelle_email: pendingProfile.zelle_email || targetProfile?.zelle_email || null,
    zelle_phone: pendingProfile.zelle_phone || targetProfile?.zelle_phone || null,
    zelle_updated_at: pendingProfile.zelle_name || pendingProfile.zelle_email || pendingProfile.zelle_phone
      ? (pendingProfile.zelle_updated_at || new Date().toISOString())
      : (targetProfile?.zelle_updated_at || null),
    notes: pendingProfile.notes || targetProfile?.notes || null,
    applied_at: pendingProfile.applied_at || targetProfile?.applied_at || new Date().toISOString(),
  };

  const { error: upsertProfileError } = await supabase
    .from('profiles')
    .upsert(mergedProfile, { onConflict: 'id' });

  if (upsertProfileError) return { error: upsertProfileError };

  if (targetUserId !== pendingProfileId) {
    const reassignedDocs = applicationDocuments.map((document) => ({
      ...document,
      user_id: targetUserId,
    }));
    const { error: moveDocsError } = await supabase
      .from('subscriber_application_documents')
      .upsert(reassignedDocs, { onConflict: 'user_id,document_slug,version' });
    if (moveDocsError) return { error: moveDocsError };

    const { error: cleanupDocsError } = await supabase
      .from('subscriber_application_documents')
      .delete()
      .eq('user_id', pendingProfileId);
    if (cleanupDocsError) return { error: cleanupDocsError };

    await supabase
      .from('subscriber_delivery_log')
      .update({ user_id: targetUserId })
      .eq('user_id', pendingProfileId);

    await supabase
      .from('profiles')
      .update({
        status: 'rejected',
        notes: [
          pendingProfile.notes?.trim(),
          `Merged into auth user ${targetUserId} during manager approval (${resolutionMode}).`,
        ].filter(Boolean).join('\n\n'),
      })
      .eq('id', pendingProfileId);
  }

  const fund = await getFundState();
  if (!fund) return { error: { message: 'Fund state not found' } };

  const amount = Number(mergedProfile.requested_amount) || 0;
  if (amount <= 0) return { error: { message: 'No investment amount specified' } };

  const { error: createError } = await createInvestorState({
    userId: targetUserId,
    fundId: fund.id,
    units: 0,
    investedAmount: amount,
    protectionType: mergedProfile.requested_protection || 'none',
    name: mergedProfile.username || null,
  });

  if (createError) return { error: createError };

  const destination = null;
  const targetDocuments = applicationDocuments.map((document) => ({
    ...document,
    user_id: targetUserId,
  }));
  for (const document of targetDocuments) {
    await logSubscriberDelivery({
      userId: targetUserId,
      documentSlug: document.document_slug,
      title: document.title,
      destination,
      details: {
        notification_type: 'subscriber_approval_document_delivery',
        version: document.version,
        snapshot_path: document.snapshot_path,
      },
    });
  }

  await logSubscriberDelivery({
    userId: targetUserId,
    title: 'Application approved',
    destination,
    details: {
      notification_type: 'subscriber_approval_notice',
      document_count: targetDocuments.length,
      resolution_mode: resolutionMode,
      source_profile_id: pendingProfileId,
    },
  });

  return { error: null, userId: targetUserId };
};


// ── REJECT APPLICATION ────────────────────────────────────
// Manager rejects a pending investor.
// Updates profiles.status → 'rejected'.
export const rejectApplication = async (userId) => {
  const { error } = await supabase
    .from('profiles')
    .update({ status: 'rejected' })
    .eq('id', userId);

  return { error };
};


// ── GET PROFILE STATUS ────────────────────────────────────
// Returns the current user's approval status from profiles.
// Used by ProtectedRoute to block pending investors.
export const getProfileStatus = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('status')
    .eq('id', userId)
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;
  return data.status || 'approved'; // default for legacy profiles
};


// ── OVERRIDE HWM (EMERGENCY) ─────────────────────────────
// Allows the manager to manually set the high water mark.
// This is an emergency escape hatch — not part of normal operations.
//
// PARAMETERS:
//   fundId  — fund_state.id
//   newHwm  — the new high water mark value
//   reason  — required audit note explaining why
//
// Also logs the override to nav_history as an audit trail entry.
export const overrideHWM = async (fundId, newHwm, reason) => ({
  error: { message: 'High-water-mark overrides were removed. Use Fund Value Settlement for dollar-only accounting.' },
  fundId,
  requestedValue: newHwm,
  reason,
});

// eslint-disable-next-line no-unused-vars
const legacyOverrideHWM = async (fundId, newHwm, reason) => {
  if (!reason || !reason.trim()) {
    return { error: { message: 'A reason is required for HWM overrides' } };
  }
  if (typeof newHwm !== 'number' || newHwm <= 0) {
    return { error: { message: 'HWM must be a positive number' } };
  }

  // Get current fund state for the audit trail
  const fund = await getFundState();
  if (!fund) return { error: { message: 'Fund state not found' } };

  const oldHwm = Number(fund.high_water_mark);

  // Update HWM in fund_state
  const { error: updateError } = await supabase
    .from('fund_state')
    .update({
      high_water_mark: newHwm,
      // If new HWM is at or above current NAV, we're in recovery mode
      // If new HWM is below current NAV, we're NOT in recovery mode
      recovery_mode: newHwm >= Number(fund.current_nav),
    })
    .eq('id', fundId);

  if (updateError) return { error: updateError };

  // Log the override to nav_history as an audit entry
  await supabase
    .from('nav_history')
    .insert({
      fund_id: fundId,
      date:    new Date().toISOString().split('T')[0],
      nav:     Number(fund.current_nav),
      // Store the override note — this column may not exist yet,
      // so we catch any error silently
    });

  return {
    error: null,
    oldHwm,
    newHwm,
    reason: reason.trim(),
  };
};
